<template>
    <div v-if="pageData.data" class="climate-protest-tracker">
        <div class="zone-title zone-title--bg banded landing__zone-title--short">
            <div class="container-title container-headline container-headline--no-image">
                <h2>{{ pageData.data.title }}</h2>
                <RichTextElements :elements="pageData.data.dek" class="interactive-dek" bodyStyle="serif-large" />
            </div>
        </div>
        <div class="section map">
            <div id="chartdiv"></div>
            <div id="info"></div>
            <div id="aboutTab">
                <div id="aboutTabContent">
                    <div class="top-section">
                        <div class="close-button">
                            <button class="close-panel" id="close-about">
                                X<span class="hidden">Close</span><span class="ceip-icon icon-cancel-circle"></span>
                            </button>
                        </div>
                        <div class="foreground">
                            <ul class="nav nav-tabs">
                                <li class="active">
                                    <div class="tab-clicks" @click="openTab('aboutTracker', $event)">About</div>
                                </li>
                                <li>
                                    <div class="tab-clicks" @click="openTab('definitions', $event)">Definitions</div>
                                </li>
                                <li>
                                    <div class="tab-clicks" @click="openTab('limitations', $event)">Limitations</div>
                                </li>
                                <li>
                                    <div class="tab-clicks" @click="openTab('sources', $event)">Data Sources</div>
                                </li>
                            </ul>
                        </div>
                    </div>
                    <div class="section about-overlay">
                        <div class="foreground">
                            <div class="tab-content">
                                <div class="tab-pane fade in active" role="tabpanel" id="aboutTracker">
                                    <div class="component">
                                        <div class="gutter-bottom">
                                            <p>
                                                Carnegie’s Climate Protest Tracker identifies episodes of mass activism
                                                relating to climate policy around the world since 2022. Building on
                                                Carnegie’s existing protest research, the Climate Protest Tracker aggregates
                                                key climate-related protests around the world and presents their objectives,
                                                participants, and outcomes. Designed for researchers, decisionmakers, and
                                                journalists, this resource helps illustrate how demands around climate
                                                policy are spurring civic action.
                                            </p>
                                        </div>
                                        <div class="gutter-bottom">
                                            <h4>More related research from Carnegie</h4>
                                            <a
                                                class="button"
                                                href="https://carnegieendowment.org/publications/interactive/protest-tracker"
                                                target="_blank"
                                                >Visit the Global Protest Tracker</a
                                            >
                                            <a
                                                class="button"
                                                href="https://carnegieendowment.org/programs/climate"
                                                target="_blank"
                                                >Visit the Sustainability, Climate, and Geopolitics Program</a
                                            >
                                        </div>
                                    </div>
                                </div>
                                <div class="tab-pane fade in" role="tabpanel" id="definitions">
                                    <div class="component">
                                        <div class="cols gutter-bottom">
                                            <div class="col col-50">
                                                <div class="gutter-right">
                                                    <strong>Country </strong>
                                                    <p>The country/countries in which the protest took place.</p>
                                                    <strong>Protest Name </strong>
                                                    <p>
                                                        Protests’ commonly used name, if any (for example, the Global
                                                        Climate Strike), or an abbreviated description of the protests.
                                                    </p>
                                                    <strong>Start Date </strong>
                                                    <p>Presents the month(s) in which the protest took place.</p>
                                                    <strong>Peak Size </strong>
                                                    <p>
                                                        An estimate, based on media reports, of the peak protest attendance.
                                                    </p>
                                                </div>
                                            </div>
                                            <div class="col col-50">
                                                <div class="gutter-left">
                                                    <strong>Key Participants </strong>
                                                    <p>Identifies groups that played a notable role in the protest.</p>
                                                    <strong>Objectives </strong>
                                                    <p>Summarizes protesters’ reason(s) for protesting.</p>
                                                    <strong>Significant Outcomes </strong>
                                                    <p>
                                                        Where applicable, identifies notable material outcomes (such as the closure of a coal power plant or consequences from a blockade), notable statements (like commitments from government or corporate actors), or policy changes (such as introducing new legislation or reforming current laws).
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="tab-pane fade in" role="tabpanel" id="limitations">
                                    <div class="component">
                                        <div class="cols gutter-bottom">
                                            <div class="col col-50">
                                                <div class="gutter-right">
                                                    <strong>Comprehensiveness </strong>
                                                    <p>
                                                        This tracker focuses on mass action relating to climate, and thus
                                                        does not include many of the smaller-scale mobilizations that
                                                        attract significant attention. It also is grounded in
                                                        English-language sources and may consequently not include protests
                                                        not widely discussed in English-language media.
                                                    </p>
                                                    <strong>Protest Size </strong>
                                                    <p>
                                                        Reliable and accurate information on the number of protesters is not
                                                        always available. In many cases, the only sources of information on
                                                        a protest’s size are local authorities, who often underestimate the
                                                        size of protests, or protest organizers, who may overestimate the
                                                        size of protests. The estimate in this field reflects a best
                                                        estimate of protest attendance.
                                                    </p>
                                                </div>
                                            </div>
                                            <div class="col col-50">
                                                <div class="gutter-left">
                                                    <strong>Definitional Judgments</strong>
                                                    <p>
                                                        The criteria for inclusion in this tracker is that a protest must be
                                                        an episode of mass action around climate policy that registers in
                                                        national or international sociopolitical life. As such, the
                                                        tracker’s editors must determine what relates to “climate policy.”
                                                        The editors have generally viewed any action either calling for more
                                                        aggressive climate action or criticizing existing climate
                                                        approaches—whether aimed at a government, corporation, or any other
                                                        group—as falling under this definition. This also includes protests
                                                        that might criticize the consequences of an existing climate policy,
                                                        as in various protests around farm emissions restrictions.
                                                        Additionally, this tracker may exclude protests that relate to
                                                        climate change (for example, protests about water shortages), but
                                                        that are not framed primarily in terms of climate policy.
                                                    </p>
                                                    <strong>Reliance on English-language sources</strong>
                                                    <p>
                                                        Data for this tracker are drawn from English-language news sources.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="tab-pane fade in" role="tabpanel" id="sources">
                                    <div class="component">
                                        <p>
                                            The data in this tracker are drawn from major English-language, French-language,
                                            and Mandarin-Chinese language international media outlets. Publications and
                                            television outlets used to compile this data include (but are not limited to) Al
                                            Jazeera, BBC, Bloomberg, CNN, DW News, the <em>Economist</em>, Euronews,
                                            <em>Financial Times</em>, <em>Foreign Affairs</em>, <em>Foreign Policy</em>,
                                            France24, the <em>Guardian</em>, the <em>Nation</em>, NBC News, the
                                            <em>New York Times</em>, NPR, Reuters, Radio Free Europe/Radio Liberty, Vox, the
                                            <em>Wall Street Journal</em>, and the <em>Washington Post</em>.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <h3 class="active" id="aboutTabClick">About <span class="hide-mobile">the Tracker</span></h3>
            </div>
        </div>
        <div class="section filter-area">
			<div class="climate-type">
						<span class="climate anti"><svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 97.65 97.65"><circle style="fill:#ec1c2d;" cx="48.83" cy="48.83" r="48.83"/><text style="fill:#fff;font-family:Whitney-Black, Whitney;font-size:81.72px;font-weight:800;" transform="translate(12.08 73.88)"><tspan x="0" y="0">—</tspan></text></svg></span> Anti-Climate Protest
					</div>
            <div class="filters">
                <div class="all-filters">
                    <strong>&nbsp;</strong>
                    <form
                        class="clean-form climate-radio-all toggle-wrapper padded-less"
                        name="climate-radio-all"
                        id="climate-radio-all"
                        action=""
                        method="get"
                        enctype="multipart/form-data"
                        autocomplete="off"
                    >
                        <fieldset>
                            <div class="control-group">
                                <label class="pub-type-label active" id="all">
                                    <span class="name">Show All</span>
                                    <input
                                        class="checkradios"
                                        type="radio"
                                        value="all"
                                        name="radio-group"
                                        id="radio-all"
                                        @click="allMotivationInput"
                                    />
                                </label>
                            </div>
                        </fieldset>
                    </form>
                </div>
                <div class="participants-filters">
                    <strong>Filter protests by participant type:</strong>
                    <form
                        class="clean-form climate-radio toggle-wrapper padded-less"
                        name="climate-radio"
                        id="climate-radio"
                        action=""
                        method="get"
                        enctype="multipart/form-data"
                        autocomplete="off"
                    >
                        <fieldset>
                            <div class="control-group">
                                <label class="pub-type-label" id="youth">
                                    <span class="name">Youth</span>
                                    <input
                                        class="checkradios"
                                        type="radio"
                                        value="youth"
                                        name="radio-group"
                                        id="radio-youth"
                                        @click="youthMotivationInput"
                                    />
                                </label>
                                <label class="pub-type-label" id="labor">
                                    <span class="name">Labor</span>
                                    <input
                                        class="checkradios"
                                        type="radio"
                                        value="labor"
                                        name="radio-group"
                                        id="radio-labor"
                                        @click="laborMotivationInput"
                                    />
                                </label>
                                <label class="pub-type-label" id="indigenous">
                                    <span class="name">Indigenous</span>
                                    <input
                                        class="checkradios"
                                        type="radio"
                                        value="indigenous"
                                        name="radio-group"
                                        id="radio-indigenous"
                                        @click="indigeneousMotivationInput"
                                    />
                                </label>
                                <label class="pub-type-label" id="parties">
                                    <span class="name">Political Parties</span>
                                    <input
                                        class="checkradios"
                                        type="radio"
                                        value="parties"
                                        name="radio-group"
                                        id="radio-parties"
                                        @click="partiesMotivationInput"
                                    />
                                </label>
                            </div>
                        </fieldset>
                    </form>
                </div>
                <div class="other-filters">
                    <strong>Other filters:</strong>
                    <form
                        class="clean-form climate-radio-other toggle-wrapper padded-less"
                        name="climate-radio-other"
                        id="climate-radio-other"
                        action=""
                        method="get"
                        enctype="multipart/form-data"
                        autocomplete="off"
                    >
                        <fieldset>
                            <label class="pub-type-label" id="large">
                                <span class="name">&gt;10,000 Participants</span>
                                <input
                                    class="checkradios"
                                    type="radio"
                                    value="large"
                                    name="radio-group"
                                    id="radio-large"
                                    @click="largeMotivationInput"
                                />
                            </label>
                            <label class="pub-type-label" id="significant">
                                <span class="name">Significant Outcomes</span>
                                <input
                                    class="checkradios"
                                    type="radio"
                                    value="significant"
                                    name="radio-group"
                                    id="radio-significant"
                                    @click="significantMotivationInput"
                                />
                            </label>
                        </fieldset>
                    </form>
                </div>
            </div>
        </div>
        <div class="section results-table" id="resultsTable">
            <table id="protestTable">
                <thead id="headings">
                    <tr>
                        <th class="protest-name sortable active" data-attr="country" data-asc="true">Country</th>
                        <th class="responsive-hide">Protest Name</th>
                        <th class="sortable" data-attr="date">Start Date</th>
                        <th class="sortable" data-attr="size">Peak Size</th>
                        <th class="participants">Key Participants</th>
                        <th class="objectives responsive-hide">Objectives</th>
                        <th class="outcomes responsive-hide">Significant Outcomes</th>
                    </tr>
                </thead>
                <tbody id="table-results"></tbody>
            </table>
        </div>
        <div class="section download-buttons">
            <div class="foreground">
                <a
                    class="button"
                    href="https://docs.google.com/spreadsheets/d/1alJFhmp_fIzmRRl0s2OSAlsxwglTNF05vskgPwjRLTM/edit?usp=sharing"
                    target="_blank"
                    >Download Spreadsheet</a
                >
                <a
                    class="button"
                    href="https://carnegieendowment.org/publications/interactive/protest-tracker"
                    target="_blank"
                    >Visit the Global Protest Tracker</a
                >
            </div>
        </div>
    </div>
</template>

<script setup>
useHead({
    script: [
        { innerHTML: '', src: 'https://cdn.amcharts.com/lib/5/index.js' },
        { innerHTML: '', src: 'https://cdn.amcharts.com/lib/5/map.js' },
        { innerHTML: '', src: 'https://cdn.amcharts.com/lib/5/geodata/worldLow.js' },
        { innerHTML: '', src: 'https://cdn.amcharts.com/lib/5/themes/Animated.js' },
    ],
});
import { onMounted, ref } from 'vue';
import FeatureFragment from '@fragments/pages/FeatureFragment.ts';

const pageData = await usePageData(FeatureFragment, 'features');

let usedDataArray;
let responsive = false;
let mobile = false;

// does this need to happen any time other than on load?  We could repeat with each button, just not sure it's worth it ...
function getResponsive() {
    const windowWidth = ref(window.innerWidth);
    if (windowWidth.value <= 767) {
        mobile = true;
    } else if (windowWidth.value <= 1024) {
        responsive = true;
    }
}
// anything that needs the DOM to exist (like calling document or window) has to be called here
let trackedCountry = '#00a79d';
let filteredCountry = '#00365e';
let inactiveCountry = '#99dcd8';
let clickedCountry = '#072a42';

onMounted(() => {
    trackedCountry = am5.color(0x00a79d);
    filteredCountry = am5.color(0x00365e);
    inactiveCountry = am5.color(0x99dcd8);
    clickedCountry = am5.color(0x072a42);
    getResponsive();
    renderResults();
    aboutTab();
    callMap();
    // I need to catch any time a sortable is clicked, so it looks for all clicks on anything and acts if the className includes sortable ....
    document.onclick = function (e) {
        if (e.target.className && e.target.className.indexOf('sortable') != -1) {
            sortFunction(e);
        }
        if (e.target.className && e.target.className.indexOf('content-toggle') != -1) {
            openNextRow(e);
        }
    };
});

const dataArray = [
	{
		id: [
			{id: "KR"}
		],
		arrayItem: true,
		country: 'South Korea',
		listCountry: 'South Korea',
		protest_name: '<span class="protest">South Korea Lack of Climate Progress Protest</span>',
		date: '2024/9/07',
		date_text: "September 2024",
		objective: 'To demand more agressive climate legislation, protesters weathered extreme heat in Seoul. ',
		size: 30000,
		size_text: '30,000',
		participants: '907 Climate Justice March Group Committee',
		outcomes: '',
		country_text: "",
		youth: true,
		large: true,
		proClimate: true,
		mapData: [
			{
				"id": "KR", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
			{
				"id": "KR", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "GB"}
		],
		arrayItem: true,
		country: 'United Kingdom',
		listCountry: 'United Kingdom',
		protest_name: '<span class="protest">Windsor Upgrade Democracy 3 Day Demonstration</span>',
		date: '2024/8/30',
		date_text: "August 2024",
		objective: 'To oppose political institutions that the protesters believe are corrupt and prioritizing "planet over profit;" the group marched to promote the "upgrade" of democracy.',
		size: 1001,
		size_text: '>1,000',
		participants: 'Extinction Rebellion',
		outcomes: '',
		country_text: "",
		youth: true,
		indigenous: true,
		proClimate: true,
		mapData: [
			{
				"id": "GB", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
			{
				"id": "GB", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "US"}
		],
		arrayItem: true,
		country: 'United States',
		listCountry: 'United States',
		protest_name: '<span class="protest">DNC Protest</span>',
		date: '2024/8/19',
		date_text: "August 2024",
		objective: 'To pressure Kamala Harris to make changes to her platform that include calling for a ceasefire in Gaza, expanding climate action, and protecting reproductive rights.  ',
		size: 1001,
		size_text: '>1,000',
		participants: '',
		outcomes: '',
		country_text: "",
		proClimate: true,
		mapData: [
			{
				"id": "US", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
			{
				"id": "US", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "AU"}
		],
		arrayItem: true,
		country: 'Australia',
		listCountry: 'Australia',
		protest_name: '<span class="protest">Australia Parliament Occupation</span>',
		date: '2024/8/14',
		date_text: "August 2024",
		objective: 'To oppose the approval of new coal and gas projects, protesters occupied the Parliament House in Canberra. ',
		size: 10,
		size_text: '100',
		participants: 'Move Beyond Coal, 350.org, Rising Tide',
		outcomes: '',
		country_text: "",
		proClimate: true,
		mapData: [
			{
				"id": "AU", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
			{
				"id": "AU", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "GB"}
		],
		arrayItem: true,
		country: 'Ireland',
		listCountry: 'Ireland',
		protest_name: '<span class="protest">Ireland &lsquo;Vomiting&rsquo; Protest</span>',
		date: '2024/8/11',
		date_text: "August 2024",
		objective: 'To highlight the major impact of factory farming and commercial agriculture on the climate crisis, protesters simulated a mass vomiting event of green liquid.',
		size: 101,
		size_text: '>100',
		participants: 'Slí Eile',
		outcomes: '',
		country_text: "",
		proClimate: true,
		mapData: [
			{
				"id": "GB", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
			{
				"id": "GB", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "RS"}
		],
		arrayItem: true,
		country: 'Serbia',
		listCountry: 'Serbia',
		protest_name: '<span class="protest">Thousands protest against lithium mining in Serbia</span>',
		date: '2024/8/10',
		date_text: "August 2024",
		objective: 'To oppose plans to mine a large lithium deposit in Serbia’s Jadar Valley.',
		size: 27000,
		size_text: '27,000',
		participants: '',
		outcomes: '',
		country_text: "",
		youth: true,
		large: true,
		proClimate: true,
		mapData: [
			{
				"id": "RS", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
			{
				"id": "RS", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "DE"}
		],
		arrayItem: true,
		country: 'Germany',
		listCountry: 'Germany',
		protest_name: '<span class="protest">Borkum Anti-Gas Drilling in the North Sea Protest Continued</span>',
		date: '2024/8/10',
		date_text: "August 2024",
		objective: 'To oppose gas drilling in the North Sea, Greenpeace activists demonstrated on Borkum alongside Fridays for Future; Greenpeace activists were at sea for two days.',
		size: 2000,
		size_text: '2,000',
		participants: 'Fridays for Future, Greenpeace, Luisa Neubauer',
		outcomes: '',
		country_text: "",
		youth: true,
		proClimate: true,
		mapData: [
			{
				"id": "DE", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
			{
				"id": "DE", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "US"}
		],
		arrayItem: true,
		country: 'United States',
		listCountry: 'United States',
		protest_name: '<span class="protest">Boston “TIME IS UP” Climate Rally</span>',
		date: '2024/07/14',
		date_text: "July 2024",
		objective: 'To encourage the Massachusetts legislature to pass climate legislation ahead of the end of the legislative session on July 31. ',
		size: 100,
		size_text: '100',
		participants: 'Mass Power Forward, Massachusetts Youth Climate Coalition',
		outcomes: '',
		country_text: "",
		youth: true,
		proClimate: true,
		mapData: [
			{
				"id": "US", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
			{
				"id": "US", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "GB"}
		],
		arrayItem: true,
		country: 'Scotland',
		listCountry: 'Scotland',
		protest_name: '<span class="protest">St. Fittick’s Park Encampment</span>',
		date: '2024/07/11',
		date_text: "July 2024",
		objective: 'To oppose the establishment of an Energy Transition Zone in St. Fittick’s Park which they believe deploys unproven technology and will negatively impact community green space. The ETZ is being developed by industry to attract renewable energy investment.',
		size: 100,
		size_text: '100',
		participants: 'Friends of St. Fittick’s Park',
		outcomes: '',
		country_text: "",
		proClimate: true,
		mapData: [
			{
				"id": "GB", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
			{
				"id": "GB", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "KR"}
		],
		arrayItem: true,
		country: 'South Korea',
		listCountry: 'South Korea',
		protest_name: '<span class="protest">South Korea Agricultural Demonstration</span>',
		date: '2024/07/04',
		date_text: "July 2024",
		objective: 'To demand reforms to agricultural policy in the context of recurring climate disasters and to draw attention to the changing nature of agricultural work.',
		size: 3000,
		size_text: '3,000',
		participants: 'The Korean Peasant League (KPL) and the Korean Peasant Women’s Association (KWPA)',
		outcomes: '',
		country_text: "",
		labor: true,
		proClimate: true,
		mapData: [
			{
				"id": "KR", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
			{
				"id": "KR", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "AT"}
		],
		arrayItem: true,
		country: 'Austria',
		listCountry: 'Austria',
		protest_name: '<span class="protest">Austria Cyclist Obstruction</span>',
		date: '2024/06/01',
		date_text: "June 2024",
		objective: 'To call for climate policy and greater investment into sustainable, green transportation infrastructure.',
		size: 2000,
		size_text: '2,000',
		participants: 'Fridays for Future',
		outcomes: '',
		country_text: "",
		proClimate: true,
		youth: true,
		mapData: [
				{
				"id": "AT", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "AT", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "ES"}
		],
		arrayItem: true,
		country: 'Spain',
		listCountry: 'Spain',
		protest_name: '<span class="protest">Sam Ignacio School Environment Day Protest</span>',
		date: '2024/06/05',
		date_text: "June 2024",
		objective: 'To petition the government to protect the future of the planet by fighting climate change and ending conflict.',
		size: 200,
		size_text: '200',
		participants: '',
		outcomes: '',
		country_text: "",
		proClimate: true,
		youth: true,
		mapData: [
				{
				"id": "ES", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "ES", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "PH"}
		],
		arrayItem: true,
		country: 'Philippines',
		listCountry: 'Philippines',
		protest_name: '<span class="protest">Asian Development Bank Protest</span>',
		date: '2024/06/05',
		date_text: "June 2024",
		objective: 'To call on the Clean Energy Forum to facilitate rapid decarbonization and transition to renewables by 2050.',
		size: 50,
		size_text: '50',
		participants: '',
		outcomes: '',
		country_text: "",
		proClimate: true,
		mapData: [
				{
				"id": "PH", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "PH", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "DE"}
		],
		arrayItem: true,
		country: 'Germany',
		listCountry: 'Germany',
		protest_name: '<span class="protest">German Bicycle Protest</span>',
		date: '2024/06/07',
		date_text: "June 2024",
		objective: 'To protest against the expansion of the Südschnellweg and demand more investment into sustainable public transportation.',
		size: 600,
		size_text: '600',
		participants: '',
		outcomes: '',
		country_text: "",
		proClimate: true,
		mapData: [
				{
				"id": "DE", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "DE", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "BR"}
		],
		arrayItem: true,
		country: 'Brazil',
		listCountry: 'Brazil',
		protest_name: '<span class="protest">Brazil World Environment Day Protest</span>',
		date: '2024/06/07',
		date_text: "June 2024",
		objective: 'To demand political change in the wake of catastrophic flooding that killed 172; protesters distributed seeds at the end of the demonstration.',
		size: 101,
		size_text: '>100',
		participants: 'Landless Workers’ Movement, Federal Deputy Maria do Rosário, Councilman Roberto Robaina, Federal Deputy Reginete Bispo, State Deputy Luciana Genro, Councilwoman Biga Pereira, Councilman Jonas Reis, technical director of Agapan Francisco Milanez',
		outcomes: '',
		country_text: "",
		proClimate: true,
		youth: true,
		labor: true, 
		parties: true,
		mapData: [
				{
				"id": "BR", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "BR", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "DE"}
		],
		arrayItem: true,
		country: 'Germany',
		listCountry: 'Germany',
		protest_name: '<span class="protest">"Climate Protection Instead of War" Protest</span>',
		date: '2024/06/08',
		date_text: "June 2024",
		objective: 'To criticize the EU for a lack of climate action and justice; to accuse the EU of warmongering, protesters also carried pro-Palestine flags.',
		size: 100,
		size_text: '100',
		participants: '',
		outcomes: '',
		country_text: "",
		proClimate: true,
		mapData: [
				{
				"id": "DE", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "DE", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "US"}
		],
		arrayItem: true,
		country: 'United States',
		listCountry: 'United States',
		protest_name: '<span class="protest">Summer of Heat Protest</span>',
		date: '2024/06/22',
		date_text: "June 2024",
		objective: 'To oppose investments by Citigroup that are propagating environmental racism and fueling the climate emergency. ',
		size: 1000,
		size_text: '1,000',
		participants: 'New York Communities for Change, Stop the Money Pipeline, Climate Defenders, Planet Over Profit, Vessel Project of Louisiana',
		outcomes: '68 arrested.',
		country_text: "",
		proClimate: true,
		mapData: [
				{
				"id": "US", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "US", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "FR"}
		],
		arrayItem: true,
		country: 'France',
		listCountry: 'France',
		protest_name: '<span class="protest">"Anti-Climaticidal Projects" Protest</span>',
		date: '2024/06/07',
		date_text: "June 2024",
		objective: 'To protest "climaticidal" projects, including industrial projects that increase carbon emissions and disrupt local ecosystems.',
		size: 10001,
		size_text: '>10,000',
		participants: 'Soulèvements de la Terre, La voie est libre, Extinction Rebellion Toulouse, ZAD A69',
		outcomes: '',
		country_text: "",
		proClimate: true,
		youth: true,
		large: true,
		mapData: [
				{
				"id": "FR", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "FR", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "IT"}
		],
		arrayItem: true,
		country: 'Italy',
		listCountry: 'Italy',
		protest_name: '<span class="protest">Italy Anti-G7 Protest</span>',
		date: '2024/06/15',
		date_text: "June 2024",
		objective: 'To criticize the failure of G7 countries to deliver on climate finance promises, to denounce escalation in the Russia-Ukraine conflict, and to argue that protectionism undermines trade liberalization. ',
		size: 1001,
		size_text: '>1,000',
		participants: 'Extinction Rebellion',
		outcomes: '',
		country_text: "",
		proClimate: true,
		youth: true,
		mapData: [
				{
				"id": "IT", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "IT", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "BE"}
		],
		arrayItem: true,
		country: 'Belgium',
		listCountry: 'Belgium',
		protest_name: '<span class="protest">"For the Climate and For Democracy" Protest</span>',
		date: '2024/06/23',
		date_text: "June 2024",
		objective: 'To demonstrate for democracy and climate policy as well as oppose the far-right and EU austerity measures. ',
		size: 500,
		size_text: '500',
		participants: 'Rise for Climate Belgium',
		outcomes: '',
		country_text: "",
		proClimate: true,
		youth: true,
		mapData: [
				{
				"id": "BE", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "BE", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "JP"},
			{id: "ID"},
			{id: "IN"},
			{id: "BD"},
			{id: "PK"},
			{id: "PH"},
		],
		arrayItem: true,
		country: 'Multiple',
		listCountry: 'Japan, Indonesia, India, Bangladesh, Pakistan, Philippines',
		protest_name: '<span class="protest">Anti-Megabanks Protest</span>',
		date: '2024/06/26',
		date_text: "June 2024",
		objective: 'To hold megabanks accountable for the purported deception of decarbonization, protesters demanded that the Mizuho Financial Group, Mitsubishi UFJ Financial Group (MUFG), and Sumitomo Mitsui Financial Group Inc. "Stop the Lies and Deception"',
		size: 50,
		size_text: '50',
		participants: 'Asian Peoples’ Movement on Debt and Development',
		outcomes: '',
		country_text: "",
		proClimate: true,
		mapData: [
			{
				"id": "JP", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "ID", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "IN", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "BD", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "PK", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "PH", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
			{
				"id": "JP", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "ID", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "IN", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "BD", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "PK", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "PH", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "UG"}
		],
		arrayItem: true,
		country: 'Uganda',
		listCountry: 'Uganda',
		protest_name: '<span class="protest">Anti-EACOP Demonstrations</span>',
		date: '2024/06/01',
		date_text: "June 2024",
		objective: 'To protest the East African Crude Oil Pipeline, which is a TotalEnergies project, and discourage Chinese companies from backing the project. Demonstrators from over 12 cities participated, including Abuja, Kinshasa, Nairobi, and Tshwane.',
		size: 50,
		size_text: '50',
		participants: '',
		outcomes: '33 arrested.',
		country_text: "",
		proClimate: true,
		mapData: [
				{
				"id": "UG", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "UG", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "NL"}
		],
		arrayItem: true,
		country: 'Netherlands',
		listCountry: 'Netherlands',
		protest_name: '<span class="protest">The Climate Chain Campaign</span>',
		date: '2024/06/01',
		date_text: "June 2024",
		objective: 'To raise awareness about climate change, activists formed a human chain on the beach.',
		size: 30000,
		size_text: '30,000',
		participants: 'Urgenda Foundation',
		outcomes: '',
		country_text: "",
		proClimate: true,
		large: true,
		mapData: [
				{
				"id": "NL", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "NL", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "GB"}
		],
		arrayItem: true,
		country: 'United Kingdom',
		listCountry: 'United Kingdom',
		protest_name: '<span class="protest">London Restore Nature Now March</span>',
		date: '2024/06/01',
		date_text: "June 2024",
		objective: 'To pressure the next UK administration to listen to public demands for climate action.',
		size: 60001,
		size_text: '>60,000',
		participants: '350+ environmental groups, Wiltshire Wildlife, naturist Chris Packham, actress Emma Thompson, former Green Party MP Caroline Lucas, and ornithologist and environmentalist Dr Mya-Rose Craig (aka Birdgirl)',
		outcomes: '',
		country_text: "",
		proClimate: true,
		youth: true,
		indigenous: true,
		large: true,
		mapData: [
				{
				"id": "GB", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "GB", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "US"}
		],
		arrayItem: true,
		country: 'United States',
		listCountry: 'United States',
		protest_name: '<span class="protest">Summer of Heat Campaign Third Act</span>',
		date: '2024/06/13',
		date_text: "June 2024",
		objective: 'To continue opposition to Citibank in New York as a part of the Summer of Heat direct action protests; elders attended with rocking chairs.',
		size: 200,
		size_text: '200',
		participants: 'Third Act',
		outcomes: '60 Third Actors arrested. ',
		country_text: "",
		proClimate: true,
		mapData: [
				{
				"id": "US", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "US", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "US"}
		],
		arrayItem: true,
		country: 'United States',
		listCountry: 'United States',
		protest_name: '<span class="protest">Wall Street Protest at Citi HQ</span>',
		date: '2024/06/11',
		date_text: "June 2024",
		objective: 'To protest investments in fossil fuels that they say are killing the planet.',
		size: 101,
		size_text: '>100',
		participants: '',
		outcomes: '23 arrested.',
		country_text: "",
		proClimate: true,
		mapData: [
				{
				"id": "US", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "US", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "FI"}
		],
		arrayItem: true,
		country: 'Finland',
		listCountry: 'Finland',
		protest_name: '<span class="protest">Helsinki Extinction Rebellion Protest</span>',
		date: '2024/06/07',
		date_text: "June 2024",
		objective: 'To shed greater light on the climate conversation.',
		size: 1000,
		size_text: '1,000',
		participants: 'Elokapina (Extinction Rebellion Chapter in Finland)',
		outcomes: '',
		country_text: "",
		youth: true,
		proClimate: true,
		mapData: [
				{
				"id": "FI", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "FI", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "EC"}
		],
		arrayItem: true,
		country: 'Ecuador',
		listCountry: 'Ecuador',
		protest_name: '<span class="protest">Quito Gas Flares Protest</span>',
		date: '2024/06/05',
		date_text: "June 2024",
		objective: 'To demand that companies comply with a court order to halt the use of gas flares by oil producers.',
		size: 51,
		size_text: '>50',
		participants: '',
		outcomes: '',
		country_text: "",
		indigenous: true,
		proClimate: true,
		mapData: [
				{
				"id": "EC", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "EC", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "BE"}
		],
		arrayItem: true,
		country: 'Belgium',
		listCountry: 'Belgium',
		protest_name: '<span class="protest">Farmers Protest Ahead of EU Election</span>',
		date: '2024/06/04',
		date_text: "June 2024",
		objective: 'To advocate for agricultural investment ahead of EU parliament elections.',
		size: 501,
		size_text: '>500',
		participants: 'Farmers Defence Force',
		outcomes: '',
		country_text: "",
		labor: true,
		antiClimate: true,
		mapData: [
				{
				"id": "BE", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "BE", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "DE"}
		],
		arrayItem: true,
		country: 'Germany ',
		listCountry: 'Germany ',
		protest_name: '<span class="protest">Berlin Climate Rally Ahead of EU Elections</span>',
		date: '2024/05/31',
		date_text: "May 2024",
		objective: 'To demonstrate in favor of climate protection and democracy preservation.',
		size: 5001,
		size_text: '>5,000',
		participants: 'Fridays for Future',
		outcomes: '',
		country_text: "",
		youth: true,
		proClimate: true,
		mapData: [
				{
				"id": "DE", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "DE", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "FR"}
		],
		arrayItem: true,
		country: 'France',
		listCountry: 'France',
		protest_name: '<span class="protest">France Anti-Amundi and Anti-TotalEnergies Protest</span>',
		date: '2024/05/25',
		date_text: "May 2024",
		objective: 'To protest against Amundi, one of the largest investors in TotalEnergies, and oppose the construction of new oil fields.',
		size: 301,
		size_text: '>300',
		participants: 'Extinction Rebellion',
		outcomes: '175 arrested',
		country_text: "",
		youth: true,
		proClimate: true,
		mapData: [
				{
				"id": "FR", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "FR", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "CA"}
		],
		arrayItem: true,
		country: 'Canada',
		listCountry: 'Canada',
		protest_name: '<span class="protest">Westmount Student Protest</span>',
		date: '2024/05/16',
		date_text: "May 2024",
		objective: 'To demand climate action, students and teachers staged protest in Montreal.',
		size: 101,
		size_text: '>100',
		participants: '',
		outcomes: '',
		country_text: "",
		youth: true,
		labor: true,
		proClimate: true,
		mapData: [
				{
				"id": "CA", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "CA", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "PL"}
		],
		arrayItem: true,
		country: 'Poland',
		listCountry: 'Poland',
		protest_name: '<span class="protest">Down with the Green Deal Protest in Poland</span>',
		date: '2024/05/10',
		date_text: "May 2024",
		objective: 'To demand the EU government stops pursuing a climate agenda and the Green New Deal.',
		size: 10001,
		size_text: '>10,000',
		participants: 'Solidarity- a farmer trade union',
		outcomes: '',
		country_text: "",
		large: true,
		labor: true,
		antiClimate: true,
		mapData: [
				{
				"id": "PL", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "PL", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "DE"}
		],
		arrayItem: true,
		country: 'Germany',
		listCountry: 'Germany',
		protest_name: '<span class="protest">German Tesla Plant Protest</span>',
		date: '2024/05/10',
		date_text: "May 2024",
		objective: 'To oppose the presence of Tesla’s Grunheide factory due to complaints about water consumption and local environmental degradation; protesters argue that there needs to be investment in public transportation instead of private cars.',
		size: 1001,
		size_text: '>1,000',
		participants: '',
		outcomes: 'Tesla factory was shut down for one day.',
		country_text: "",
		significant: true,
		antiClimate: true,
		mapData: [
				{
				"id": "DE", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "DE", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "AU"}
		],
		arrayItem: true,
		country: 'Australia',
		listCountry: 'Australia',
		protest_name: '<span class="protest">Australia Rise Up Campaign</span>',
		date: '2024/05/08',
		date_text: "May 2024",
		objective: 'To demand the cessation of fossil fuel projects through the "no more coal and gas" campaign, protesters rallied outside the Kirribilli House.',
		size: 101,
		size_text: '>100',
		participants: 'Australian Youth Climate Coalition (AYCC), Move Beyond Coal, 350.org Australia, GetUp and the Australian Conservation',
		outcomes: '',
		country_text: "",
		youth: true,
		proClimate: true,
		mapData: [
				{
				"id": "AU", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "AU", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "IT"}
		],
		arrayItem: true,
		country: 'Italy',
		listCountry: 'Italy',
		protest_name: '<span class="protest">Turin, Italy G7 Protest</span>',
		date: '2024/04/28',
		date_text: "April 2024",
		objective: 'To protest at the G-7 in Turin and demand ambitious climate action that aligns with country pledges.',
		size: 101,
		size_text: '>100',
		participants: '',
		outcomes: 'Ministers at the G7 vowed to strengthen energy security and keep warming to 1.5 °C.',
		country_text: "",
		significant: true,
		proClimate: true,
		mapData: [
				{
				"id": "IT", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "IT", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "CA"}
		],
		arrayItem: true,
		country: 'Canada',
		listCountry: 'Canada',
		protest_name: '<span class="protest">Earth Day Protest Canada</span>',
		date: '2024/04/21',
		date_text: "April 2024",
		objective: 'To denounce climate inaction on Earth Day, protesters gathered in Montreal.',
		size: 101,
		size_text: '>100',
		participants: '',
		outcomes: '',
		country_text: "",
		youth: true,
		proClimate: true,
		mapData: [
				{
				"id": "CA", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "CA", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "CH"}
		],
		arrayItem: true,
		country: 'Switzerland',
		listCountry: 'Switzerland',
		protest_name: '<span class="protest">Switzerland Global Strike</span>',
		date: '2024/04/20',
		date_text: "April 2024",
		objective: 'To denounce political inaction on climate change and critique fossil fuel funding; participated in Global Strike.',
		size: 5001,
		size_text: '5,000',
		participants: 'The Elders for the Climate',
		outcomes: '',
		country_text: "",
		youth: true,
		proClimate: true,
		mapData: [
				{
				"id": "CH", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "CH", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "US"}
		],
		arrayItem: true,
		country: 'United States',
		listCountry: 'United States',
		protest_name: '<span class="protest">Climate Emergency Declaration Protest</span>',
		date: '2024/04/19',
		date_text: "April 2024",
		objective: 'To demand that Joe Biden declare a climate emergency.',
		size: 101,
		size_text: '>100',
		participants: 'Fridays for Future',
		outcomes: 'No formal climate emergency declaration has been made.',
		country_text: "",
		youth: true,
		significant: true,
		proClimate: true,
		mapData: [
				{
				"id": "US", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "US", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "SE"}
		],
		arrayItem: true,
		country: 'Sweden',
		listCountry: 'Sweden',
		protest_name: '<span class="protest">Swedish Climate Action Protest</span>',
		date: '2024/04/19',
		date_text: "April 2024",
		objective: 'To demand climate action, protesters marched in Stockholm.',
		size: 500,
		size_text: '500',
		participants: 'Fridays for Future, Greta Thunberg',
		outcomes: '',
		country_text: "",
		youth: true,
		proClimate: true,
		mapData: [
				{
				"id": "SE", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "SE", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "DE"}
		],
		arrayItem: true,
		country: 'Germany',
		listCountry: 'Germany',
		protest_name: '<span class="protest">Blockade at Freiburg Main Station</span>',
		date: '2024/04/19',
		date_text: "April 2024",
		objective: 'To advocate for greater climate protections.',
		size: 120,
		size_text: '120',
		participants: 'Last Generation',
		outcomes: '',
		country_text: "",
		proClimate: true,
		mapData: [
				{
				"id": "DE", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "DE", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "SE"}
		],
		arrayItem: true,
		country: 'Sweden',
		listCountry: 'Sweden',
		protest_name: '<span class="protest">Sweden Mother’s Rebellion Protest</span>',
		date: '2024/04/18',
		date_text: "April 2024",
		objective: 'To protest inaction on global warming, Mothers Rebellion marched with a red scarf made out of 3,000 smaller scarfs around the Swedish Parliament.',
		size: 101,
		size_text: '>100',
		participants: 'Mother’s Rebellion',
		outcomes: '',
		country_text: "",
		proClimate: true,
		mapData: [
				{
				"id": "SE", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "SE", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "PL"}
		],
		arrayItem: true,
		country: 'Poland',
		listCountry: 'Poland',
		protest_name: '<span class="protest">Polish Anti-Logging Restrictions Protest</span>',
		date: '2024/04/16',
		date_text: "April 2024",
		objective: 'To protest new logging restrictions advanced by the Polish government.',
		size: 101,
		size_text: '>100',
		participants: '',
		outcomes: '',
		country_text: "",
		labor: true,
		antiClimate: true,
		mapData: [
				{
				"id": "PL", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "PL", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "NZ"}
		],
		arrayItem: true,
		country: 'New Zealand',
		listCountry: 'New Zealand',
		protest_name: '<span class="protest">New Zealand Climate Protest</span>',
		date: '2024/04/05',
		date_text: "April 2024",
		objective: 'To keep the ban on oil and gas; provide climate education for all; protests organized across 11 locations; 500 demonstrators in Auckland.',
		size: 1001,
		size_text: '>1,000',
		participants: 'School Strike for Climate New Zealand, Friday for Future',
		outcomes: 'Protest may have been unsuccessful: New Zealand’s government is considering introducing legislation to remove offshore petroleum ban.',
		country_text: "",
		youth: true,
		significant: true,
		proClimate: true,
		mapData: [
				{
				"id": "NZ", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "NZ", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "US"}
		],
		arrayItem: true,
		country: 'United States',
		listCountry: 'United States',
		protest_name: '<span class="protest">NY HEAT Act Albany Protest</span>',
		date: '2024/03/27',
		date_text: "March 2024",
		objective: 'To demand the passage of the NY HEAT Act and the Climate Change Superfund Act, in Albany, New York.',
		size: 101,
		size_text: '>100',
		participants: 'TREEage, NY Renews',
		outcomes: '',
		country_text: "",
		youth: true,
		proClimate: true,
		mapData: [
				{
				"id": "US", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "US", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "BE"}
		],
		arrayItem: true,
		country: 'Belgium',
		listCountry: 'Belgium',
		protest_name: '<span class="protest">Brussels Farmers Protests</span>',
		date: '2024/03/26',
		date_text: "March 2024",
		objective: 'To protest EU environmental regulations; protesters were tear-gassed.',
		size: 101,
		size_text: '>100',
		participants: '',
		outcomes: '',
		country_text: "",
		labor: true,
		antiClimate: true,
		mapData: [
				{
				"id": "BE", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "BE", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "AU"}
		],
		arrayItem: true,
		country: 'Australia',
		listCountry: 'Australia',
		protest_name: '<span class="protest">Australia March in March</span>',
		date: '2024/03/24',
		date_text: "March 2024",
		objective: 'To oppose deforestation and demand better legal protections for the natural world.',
		size: 4000,
		size_text: '4,000',
		participants: 'Bob Brown Foundation, Greens senator Sarah Hanson-Young',
		outcomes: '',
		country_text: "",
		proClimate: true,
		mapData: [
				{
				"id": "AU", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "AU", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "ZA"}
		],
		arrayItem: true,
		country: 'South Africa',
		listCountry: 'South Africa',
		protest_name: '<span class="protest">South Africa Human Rights Day Climate March</span>',
		date: '2024/03/21',
		date_text: "March 2024",
		objective: 'To demand water, food, and energy for all at a Climate March on Human Rights Day; led by youth group African Climate Alliance.',
		size: 101,
		size_text: '>100',
		participants: 'African Climate Alliance',
		outcomes: '',
		country_text: "",
		youth: true,
		proClimate: true,
		mapData: [
				{
				"id": "ZA", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "ZA", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "DE"}
		],
		arrayItem: true,
		country: 'Germany',
		listCountry: 'Germany',
		protest_name: '<span class="protest">German Last Generation Bridge Blockade</span>',
		date: '2024/03/16',
		date_text: "March 2024",
		objective: 'To demand climate action; Last Generation staged a blockade on a key bridge in Germany and engaged in widespread civil disobedience; protesters appeared in 10 cities across Germany including Munich.',
		size: 130,
		size_text: '130',
		participants: 'Last Generation',
		outcomes: '',
		country_text: "",
		youth: true,
		proClimate: true,
		mapData: [
				{
				"id": "DE", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "DE", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "AU"}
		],
		arrayItem: true,
		country: 'Australia',
		listCountry: 'Australia',
		protest_name: '<span class="protest">Australia Extinction Rebellion Intersection Blockade</span>',
		date: '2024/03/16',
		date_text: "March 2024",
		objective: 'To demand action on climate change Extinction Rebellion protesters blockaded a busy intersection in Victoria.',
		size: 300,
		size_text: '300',
		participants: 'Extinction Rebellion',
		outcomes: '32 protesters arrested.',
		country_text: "",
		youth: true,
		proClimate: true,
		mapData: [
				{
				"id": "AU", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "AU", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "PL"}
		],
		arrayItem: true,
		country: 'Poland',
		listCountry: 'Poland',
		protest_name: '<span class="protest">Warsaw Farmers Protest</span>',
		date: '2024/03/06',
		date_text: "March 2024",
		objective: 'To protest EU environmental regulations and imports of cheap Ukrainian grain.',
		size: 1001,
		size_text: '>1,000',
		participants: '',
		outcomes: 'A dozen protesters were arrested; European Commission has put certain provisions of the Common Agricultural Policy under review in response to Europe farmers protests.',
		country_text: "",
		labor: true,
		significant: true,
		antiClimate: true,
		mapData: [
				{
				"id": "PL", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "PL", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "IN"}
		],
		arrayItem: true,
		country: 'India',
		listCountry: 'India',
		protest_name: '<span class="protest">Ladakh Climate Protest</span>',
		date: '2024/03/06',
		date_text: "March 2024",
		objective: 'To demand climate action from the Indian government and land autonomy in Ladakh',
		size: 1001,
		size_text: '>1,000',
		participants: '',
		outcomes: '',
		country_text: "",
		proClimate: true,
		mapData: [
				{
				"id": "IN", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "IN", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "DE"}
		],
		arrayItem: true,
		country: 'Germany',
		listCountry: 'Germany',
		protest_name: '<span class="protest">Germany Stoppt Fossile Subventionen</span>',
		date: '2024/03/02',
		date_text: "March 2024",
		objective: 'To demand the cessation of fossil fuel subsidies, Last Generation blockaded the B96 in Germany.',
		size: 230,
		size_text: '230',
		participants: 'Last Generation',
		outcomes: '',
		country_text: "",
		youth: true,
		proClimate: true,
		mapData: [
				{
				"id": "DE", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "DE", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "ES"}
		],
		arrayItem: true,
		country: 'Spain',
		listCountry: 'Spain',
		protest_name: '<span class="protest">Spain Farmers Protest</span>',
		date: '2024/02/21',
		date_text: "February 2024",
		objective: 'To protest excessive regulation and demand the loosening of the EU climate agricultural provisions.',
		size: 101,
		size_text: '>100',
		participants: '',
		outcomes: 'European Commission has put certain provisions of the Common Agricultural Policy under review in response to Europe farmers protests.',
		country_text: "",
		labor: true,
		significant: true,
		antiClimate: true,
		mapData: [
				{
				"id": "ES", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "ES", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "GR"}
		],
		arrayItem: true,
		country: 'Greece',
		listCountry: 'Greece',
		protest_name: '<span class="protest">Greece Farmers Protest </span>',
		date: '2024/02/19',
		date_text: "February 2024",
		objective: 'To protest rising fuel costs and the EU Common Agricultural Policy.',
		size: 8001,
		size_text: '8,000',
		participants: 'Farmers’ Union of Stylida',
		outcomes: 'European Commission has put certain provisions of the Common Agricultural Policy under review in response to Europe farmers protests.',
		country_text: "",
		labor: true,
		significant: true,
		antiClimate: true,
		mapData: [
				{
				"id": "GR", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "GR", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "FR"}
		],
		arrayItem: true,
		country: 'France',
		listCountry: 'France',
		protest_name: '<span class="protest">France Opposition to Oil Drilling Protest </span>',
		date: '2024/02/11',
		date_text: "February 2024",
		objective: 'To protest new oil drilling projects in Arcachon by Vermillion Energy.',
		size: 1201,
		size_text: '1,200-3,000',
		participants: 'Greta Thunberg, Stop Pétrole Bassin d&apos;Arcachon (Stop Oil Bassin Arcachon Collective)',
		outcomes: '',
		country_text: "",
		youth: true,
		proClimate: true,
		mapData: [
				{
				"id": "FR", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "FR", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "PL"}
		],
		arrayItem: true,
		country: 'Poland',
		listCountry: 'Poland',
		protest_name: '<span class="protest">Poznan Farmers Protest</span>',
		date: '2024/02/09',
		date_text: "February 2024",
		objective: 'To protest pro-climate EU policies, protesters drove tractors to the office of the regional governor in Poznan.',
		size: 1401,
		size_text: '>1,400',
		participants: '',
		outcomes: 'European Commission has put certain provisions of the Common Agricultural Policy under review in response to Europe farmers protests.',
		country_text: "",
		labor: true,
		significant: true,
		antiClimate: true,
		mapData: [
				{
				"id": "PL", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "PL", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "IT"}
		],
		arrayItem: true,
		country: 'Italy',
		listCountry: 'Italy',
		protest_name: '<span class="protest">Italy Farmers Protest</span>',
		date: '2024/02/09',
		date_text: "February 2024",
		objective: 'To protest against EU farming policies, price spikes, and reduced profit.',
		size: 101,
		size_text: '>100',
		participants: '',
		outcomes: 'European Commission has put certain provisions of the Common Agricultural Policy under review in response to Europe farmers protests.',
		country_text: "",
		labor: true,
		significant: true,
		antiClimate: true,
		mapData: [
				{
				"id": "IT", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "IT", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "ZA"}
		],
		arrayItem: true,
		country: 'South Africa',
		listCountry: 'South Africa',
		protest_name: '<span class="protest">South Africa Mining-Affected Community Protest </span>',
		date: '2024/02/08',
		date_text: "February 2024",
		objective: 'To demand inclusion in the decisionmaking process of mining projects in their communities, protesters marched to the Department of Mineral Resources and Energy head offices in Tshwan.',
		size: 101,
		size_text: '>100',
		participants: '',
		outcomes: '',
		country_text: "",
		proClimate: true,
		mapData: [
				{
				"id": "ZA", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "ZA", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "AU"}
		],
		arrayItem: true,
		country: 'Australia',
		listCountry: 'Australia',
		protest_name: '<span class="protest">Australia Rally Against Reckless Renewables Protest</span>',
		date: '2024/02/06',
		date_text: "February 2024",
		objective: 'To demand that renewable projects, namely wind turbines, are halted; led by the Rally against Reckless Renewables.',
		size: 501,
		size_text: '>500',
		participants: 'Reckless Renewables',
		outcomes: '',
		country_text: "",
		antiClimate: true,
		mapData: [
				{
				"id": "AU", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "AU", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "BE"}
		],
		arrayItem: true,
		country: 'Belgium',
		listCountry: 'Belgium',
		protest_name: '<span class="protest">Farmers European Parliament Protest</span>',
		date: '2024/02/01',
		date_text: "February 2024",
		objective: 'To shed light on the demands of farming communities that have been harmed by EU climate regulations at the European Parliament.',
		size: 101,
		size_text: '>100',
		participants: '',
		outcomes: 'European Commission has put certain provisions of the Common Agricultural Policy under review in response to Europe farmers protests.',
		country_text: "",
		labor: true,
		significant: true,
		antiClimate: true,
		mapData: [
				{
				"id": "BE", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "BE", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "US"}
		],
		arrayItem: true,
		country: 'United States',
		listCountry: 'United States',
		protest_name: '<span class="protest">Vermont Climate Protest</span>',
		date: '2024/01/27',
		date_text: "January 2024",
		objective: 'To demand the government address climate and social justice issues in Montpelier, Vermont.',
		size: 101,
		size_text: '>100',
		participants: '',
		outcomes: '',
		country_text: "",
		proClimate: true,
		mapData: [
				{
				"id": "US", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "US", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "GB"}
		],
		arrayItem: true,
		country: 'United Kingdom',
		listCountry: 'United Kingdom',
		protest_name: '<span class="protest">England Private Planes Protest</span>',
		date: '2024/01/27',
		date_text: "January 2024",
		objective: 'To protest the use of private jets in Hampshire County.',
		size: 101,
		size_text: '>100',
		participants: 'Extinction Rebellion',
		outcomes: '',
		country_text: "",
		civilSociety: true,
		youth: true,
		proClimate: true,
		mapData: [
				{
				"id": "GB", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "GB", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "NL"}
		],
		arrayItem: true,
		country: 'Netherlands',
		listCountry: 'Netherlands',
		protest_name: '<span class="protest">Netherlands Fossil Fuel Subsidy Protest </span>',
		date: '2024/01/27',
		date_text: "January 2024",
		objective: 'To protest the country’s fossil fuel subsidies, protesters created a highway blockade.',
		size: 1001,
		size_text: '>1,000',
		participants: 'Extinction Rebellion',
		outcomes: '1,000 arrests were made.',
		country_text: "",
		youth: true,
		proClimate: true,
		mapData: [
				{
				"id": "NL", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "NL", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "FR"}
		],
		arrayItem: true,
		country: 'France',
		listCountry: 'France',
		protest_name: '<span class="protest">French Farmers Protest</span>',
		date: '2024/01/26',
		date_text: "January 2024",
		objective: 'To protest state subsidies for the diesel fuel used in trucks and other farming machinery.',
		size: 70001,
		size_text: '>70,000',
		participants: '',
		outcomes: 'French government would scrap plans to reduce state subsidies on diesel fuel and cut back bureaucratic regulations farmers must follow.',
		country_text: "",
		labor: true,
		civilSociety: true,
		large: true,
		significant: true,
		antiClimate: true,
		mapData: [
				{
				"id": "FR", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "FR", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "RO"}
		],
		arrayItem: true,
		country: 'Romania',
		listCountry: 'Romania',
		protest_name: '<span class="protest">Romania Farmers Protests</span>',
		date: '2024/01/15',
		date_text: "January 2024",
		objective: 'To protest the high cost of diesel, expensive insurance rates, European Union measures to protect the environment, and pressures on the domestic market from imported Ukrainian agricultural goods.',
		size: 301,
		size_text: '>300',
		participants: '',
		outcomes: 'Representatives of the largest farm associations and Agriculture Minister Florin Barbu agreed to specific deadlines for a series of demands.',
		country_text: "",
		labor: true,
		civilSociety: true,
		significant: true,
		antiClimate: true,
		mapData: [
				{
				"id": "RO", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "RO", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "CH"}
		],
		arrayItem: true,
		country: 'Switzerland',
		listCountry: 'Switzerland',
		protest_name: '<span class="protest">World Economic Forum protest</span>',
		date: '2024/01/15',
		date_text: "January 2024",
		objective: 'To fight for climate justice during the World Economic Forum.',
		size: 350,
		size_text: '350',
		participants: '',
		outcomes: '',
		country_text: "",
		civilSociety: true,
		proClimate: true,
		mapData: [
				{
				"id": "CH", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "CH", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "NO"}
		],
		arrayItem: true,
		country: 'Norway',
		listCountry: 'Norway',
		protest_name: '<span class="protest">Deep Sea Mining Protest</span>',
		date: '2024/01/10',
		date_text: "January 2024",
		objective: 'To protest the approval of deep sea mining in Norway.',
		size: 51,
		size_text: '50–100',
		participants: 'Greenpeace Norway',
		outcomes: '',
		country_text: "",
		civilSociety: true,
		proClimate: true,
		mapData: [
				{
				"id": "NO", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "NO", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "DE"}
		],
		arrayItem: true,
		country: 'Germany',
		listCountry: 'Germany',
		protest_name: '<span class="protest">German Nationwide Farmer Blockades</span>',
		date: '2024/01/08',
		date_text: "January 2024",
		objective: 'To protest the Dutch government’s decision to phase out a tax break on agricultural diesel as tied to the German 2024 budget.',
		size: 10001,
		size_text: '>10,000',
		participants: '',
		outcomes: '',
		country_text: "",
		labor: true,
		civilSociety: true,
		large: true,
		antiClimate: true,
		mapData: [
				{
				"id": "DE", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "DE", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "NL"}
		],
		arrayItem: true,
		country: 'The Netherlands',
		listCountry: 'The Netherlands',
		protest_name: '<span class="protest">Amsterdam Highway Protest </span>',
		date: '2023/12/31',
		date_text: "December 2023",
		objective: 'To demand an end to financing fossil fuels, protesters blocked a major road; 300 were detained by police.',
		size: 501,
		size_text: '>500',
		participants: 'Organized by Extinction Rebellion',
		outcomes: '300 climate activists held by police',
		country_text: "",
		youth: true,
		civilSociety: true,
		proClimate: true,
		mapData: [
				{
				"id": "NL", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "NL", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "UG"}
		],
		arrayItem: true,
		country: 'Uganda',
		listCountry: 'Uganda',
		protest_name: '<span class="protest">Uganda Anti-EACOP Protest </span>',
		date: '2023/12/15',
		date_text: "December 2023",
		objective: 'To petition parliament to halt approval of the East African Crude Oil Pipeline.',
		size: 50,
		size_text: '50',
		participants: '',
		outcomes: '',
		country_text: "",
		youth: true,
		proClimate: true,
		mapData: [
				{
				"id": "UG", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "UG", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "PH"}
		],
		arrayItem: true,
		country: 'Philippines',
		listCountry: 'Philippines',
		protest_name: '<span class="protest">Global Day of Action for Climate Justice March</span>',
		date: '2023/12/09',
		date_text: "December 2023",
		objective: 'To demand green transition towards renewable energy and the phase out of fossil fuels.',
		size: 1001,
		size_text: '>1,000',
		participants: 'Philippine Movement for Climate Justice',
		outcomes: '',
		country_text: "",
		youth: true,
		civilSociety: true,
		proClimate: true,
		mapData: [
				{
				"id": "PH", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "PH", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "PK"}
		],
		arrayItem: true,
		country: 'Pakistan',
		listCountry: 'Pakistan',
		protest_name: '<span class="protest">Pakistan Anti-Climate Governance Protest</span>',
		date: '2023/12/09',
		date_text: "December 2023",
		objective: 'To showcase that Pakistan is on the front lines of climate change, demand equitable climate finance, and point out the government’s mismanagement of the climate crisis. ',
		size: 301,
		size_text: '>300',
		participants: '',
		outcomes: '',
		country_text: "",
		proClimate: true,
		mapData: [
				{
				"id": "PK", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "PK", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "AE"}
		],
		arrayItem: true,
		country: 'United Arab Emirates',
		listCountry: 'United Arab Emirates',
		protest_name: '<span class="protest">Dubai COP28 Protest</span>',
		date: '2023/12/05',
		date_text: "December 2023",
		objective: 'To demand the end of fossil fuels at COP28. Protesters were restricted to a small area in the blue zone. ',
		size: 101,
		size_text: '>100',
		participants: '',
		outcomes: 'A series of financial and political pledges were made: $700 million committed to loss and damage; accelerate the phase out of unabated coal; and triple renewable energy capacity by 2030.',
		country_text: "",
		youth: true,
		indigenous: true,
		civilSociety: true,
		significant: true,
		proClimate: true,
		mapData: [
				{
				"id": "AE", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "AE", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "BE"}
		],
		arrayItem: true,
		country: 'Belgium',
		listCountry: 'Belgium',
		protest_name: '<span class="protest">Brussels COP28 Protest</span>',
		date: '2023/12/03',
		date_text: "December 2023",
		objective: 'To demand more action on climate change from COP leaders.',
		size: 20001,
		size_text: '>20,000',
		participants: '',
		outcomes: '',
		country_text: "",
		youth: true,
		civilSociety: true,
		large: true,
		proClimate: true,
		mapData: [
				{
				"id": "BE", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "BE", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "AU"}
		],
		arrayItem: true,
		country: 'Australia',
		listCountry: 'Australia',
		protest_name: '<span class="protest">Australia Kayak Blockade</span>',
		date: '2023/11/25',
		date_text: "November 2023",
		objective: 'To block Australia’s largest coal port and halt the export of approximately 500,000 tonnes of coal using a 30-hour kayak blockade; 300 participants in kayaks. ',
		size: 1500,
		size_text: '1,500',
		participants: 'Rising Tide',
		outcomes: 'No coal shipments entered or exited the Port of Newcastle during the 30hr blockade.',
		country_text: "",
		civilSociety: true,
		significant: true,
		proClimate: true,
		mapData: [
				{
				"id": "AU", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "AU", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "DE"}
		],
		arrayItem: true,
		country: 'Germany',
		listCountry: 'Germany',
		protest_name: '<span class="protest">Germany Last Generation Protest</span>',
		date: '2023/11/25',
		date_text: "November 2023",
		objective: 'To promote climate awareness and demand climate action. ',
		size: 801,
		size_text: '800-1,500',
		participants: 'Last Generation, Parents Against the Fossil Industry Initiative, and researchers from Scientist Rebellion Germany',
		outcomes: '',
		country_text: "",
		youth: true,
		proClimate: true,
		mapData: [
			{
				"id": "DE", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "DE", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "UG"},
			{id: "TZ"},
			{id: "CD"},
			{id: "ZA"},
			{id: "FR"},
			{id: "US"},
			{id: "GB"}
		],
		arrayItem: true,
		country: 'Multiple',
		listCountry: 'Uganda, Tanzania, Democratic Republic of the Congo, South Africa, France, United States, United Kingdom',
		protest_name: '<span class="protest">East African Crude Oil Pipeline Project Protest </span>',
		date: '2023/11/22',
		date_text: "November 2023",
		objective: 'To demand Chinese lender institutions in East Africa to back out of the financing deal for the East African Crude Oil Pipeline (EACOP).',
		size: 101,
		size_text: '>100',
		participants: '',
		outcomes: '',
		country_text: "Countries included: Uganda, Tanzania, Democratic Republic of the Congo, South Africa, France, United States, United Kingdom ",
		civilSociety: true,
		proClimate: true,
		mapData: [
			{
				"id": "UG", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "TZ", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "CD", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "ZA", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "FR", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "US", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "GB", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
			{
				"id": "UG", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "TZ", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "CD", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "ZA", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "FR", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "US", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "GB", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "AU"}
		],
		arrayItem: true,
		country: 'Australia',
		listCountry: 'Australia',
		protest_name: '<span class="protest">Organizers School Strike 4 Climate</span>',
		date: '2023/11/17',
		date_text: "November 2023",
		objective: 'To demand the Australian federal government shift focus away from dependence on fossil fuels in Australia’s energy generation.',
		size: 1101,
		size_text: '>1,100',
		participants: '',
		outcomes: '',
		country_text: "",
		youth: true,
		proClimate: true,
		mapData: [
			{
				"id": "AU", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
			{
				"id": "AU", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "NL"}
		],
		arrayItem: true,
		country: 'Netherlands',
		listCountry: 'Netherlands',
		protest_name: '<span class="protest">Dutch General Election Climate Action Protest  </span>',
		date: '2023/11/12',
		date_text: "November 2023",
		objective: 'To demand climate action ahead of Dutch General Election; largest Dutch climate protest to date.',
		size: 70001,
		size_text: '>70,000',
		participants: 'EU climate chief Frans Timmerman (future leader of combined Greens and Labor), Greta Thunberg',
		outcomes: '',
		country_text: "",
		youth: true,
		parties: true,
		large: true,
		proClimate: true,
		mapData: [
			{
				"id": "NL", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
			{
				"id": "NL", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "KE"}
		],
		arrayItem: true,
		country: 'Kenya',
		listCountry: 'Kenya',
		protest_name: '<span class="protest">Kenya Plastic Production Protest  </span>',
		date: '2023/11/11',
		date_text: "November 2023",
		objective: 'To call for a drastic reduction in plastic production and an end to plastic pollution through a strong global plastics treaty.',
		size: 101,
		size_text: '>100',
		participants: 'Global #BreakFreeFromPlastic movement ',
		outcomes: '',
		country_text: "",
		youth: true,
		civilSociety: true,
		proClimate: true,
		mapData: [
			{
				"id": "KE", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
			{
				"id": "KE", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "ZA"}
		],
		arrayItem: true,
		country: 'South Africa',
		listCountry: 'South Africa',
		protest_name: '<span class="protest">South Africa TotalEnergies Offshore Drilling Protest </span>',
		date: '2023/11/4',
		date_text: "November 2023",
		objective: 'To demand an end to TotalEnergies (French multinational company) offshore drilling for oil and gas.',
		size: 101,
		size_text: '>100',
		participants: 'Members of Extinction Rebellion 350 Africa, Green Connection, Project 90 by 2030, and African Climate Alliance gathered outside Parliament in Cape Town  ',
		outcomes: '',
		country_text: "",
		civilSociety: true,
		proClimate: true,
		mapData: [
			{
				"id": "ZA", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
			{
				"id": "ZA", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "PA"}
		],
		arrayItem: true,
		country: 'Panama',
		listCountry: 'Panama',
		protest_name: '<span class="protest">Panama Copper mining protest</span>',
		date: '2023/11/3',
		date_text: "November 2023",
		objective: 'To protest copper mining operations conducted by Minera Panama, the local subsidiary of First Quantum Minerals in the Colon province, demonstrators engaged in a weeks long campaign.',
		size: 10001,
		size_text: '>10,000',
		participants: '',
		outcomes: 'Supreme Court of Justice ruled that Panama’s new mining contract with the Canadian company First Quantum was unconstitutional. The government annulled the contract with First Quantum and banned metal permits for the rest of 2023. ',
		country_text: "",
		significant: true,
		indigenous: true,
		civilSociety: true,
		large: true,
		antiClimate: true,
		mapData: [
			{
				"id": "PA", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
			{
				"id": "PA", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "MX"}
		],
		arrayItem: true,
		country: 'Mexico',
		listCountry: 'Mexico',
		protest_name: '<span class="protest">Grupo México’s Buenavista Mine Blockade</span>',
		date: '2023/10/26',
		date_text: "October 2023",
		objective: 'To protest the environmental and labor problems at the Cananea copper mine.',
		size: 101,
		size_text: '>100',
		participants: 'United Front',
		outcomes: 'Withdrew the blockades of the Buenavista mine after 60 hours once the government agreed to hold a meeting between the Section 65 Committee of the National Mining Union and President López Obrador.',
		country_text: "",
		significant: true,
		civilSociety: true,
		antiClimate: true,
		mapData: [
			{
				"id": "MX", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
			{
				"id": "MX", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "IN"},
			{id: "PK"},
			{id: "NP"},
			{id: "BD"},
			{id: "TH"},
			{id: "ID"},
			{id: "PH"}
		],
		arrayItem: true,
		country: 'Multiple',
		listCountry: 'India, Pakistan, Nepal, Bangladesh, Thailand, Indonesia, Philippines',
		protest_name: '<span class="protest">World Food Day 2023 with Asia Day of Action on Food and Climate</span>',
		date: '2023/10/19',
		date_text: "October 2023",
		objective: 'To promote human security, call out the inequalities in the global food system, and warn against the escalating impacts of climate change.',
		size: 1001,
		size_text: '>1,000',
		participants: '',
		outcomes: '',
		country_text: "Countries included: India, Pakistan, Nepal, Bangladesh, Thailand, Indonesia and the Philippines ",
		civilSociety: true,
		proClimate: true,
		mapData: [
			{
				"id": "IN", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "PK", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "NP", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "BD", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "TH", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "ID", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "PH", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
			{
				"id": "IN", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "PK", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "NP", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "BD", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "TH", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "ID", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "PH", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "GB"}
		],
		arrayItem: true,
		country: 'United Kingdom',
		listCountry: 'United Kingdom',
		protest_name: '<span class="protest">London Energy Intelligence Forum Protest</span>',
		date: '2023/10/17',
		date_text: "October 2023",
		objective: 'To block summit of oil and gas executives at the annual Energy Intelligence Forum.',
		size: 250,
		size_text: '250',
		participants: 'Fossil Free London, Greta Thunberg',
		outcomes: '26 individuals charged in mobilization; Greta Thunberg arrested.',
		country_text: "",
		youth: true,
		civilSociety: true,
		proClimate: true,
		mapData: [
			{
				"id": "GB", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
			{
				"id": "GB", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "SG"}
		],
		arrayItem: true,
		country: 'Singapore',
		listCountry: 'Singapore',
		protest_name: '<span class="protest">Singapore high cost of living climate protest</span>',
		date: '2023/09/25',
		date_text: "September 2023",
		objective: 'To encourage the government to protect low-income groups against the rising cost of living associated with climate change by strengthening carbon taxes and to promote energy transition away from fossil fuels. ',
		size: 1400,
		size_text: '1,400',
		participants: '',
		outcomes: '',
		country_text: "",
		proClimate: true,
		mapData: [
			{
				"id": "SG", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
			{
				"id": "SG", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "CH"}
		],
		arrayItem: true,
		country: 'Switzerland',
		listCountry: 'Switzerland',
		protest_name: '<span class="protest">Bern Protest</span>',
		date: '2023/09/30',
		date_text: "September 2023",
		objective: 'To demand tougher climate action ahead of Swiss Election.',
		size: 60001,
		size_text: '>60,000',
		participants: '',
		outcomes: '',
		country_text: "",
		large: true,
		proClimate: true,
		mapData: [
			{
				"id": "CH", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
			{
				"id": "CH", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "US"}
		],
		arrayItem: true,
		country: 'United States',
		listCountry: 'United States',
		protest_name: '<span class="protest">Fridays for Futures Campaign New York</span>',
		date: '2023/09/17',
		date_text: "September 2023",
		objective: 'To call on Biden to stop oil and gas drilling on public land and in federal waters (as promised in his 2020 campaign); prior to UN General Assembly Meeting. ',
		size: 10001,
		size_text: '>10,000',
		participants: 'Widespread Coalition, Fridays for Future',
		outcomes: '',
		country_text: "",
		youth: true,
		large: true,
		proClimate: true,
		mapData: [
			{
				"id": "US", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
			{
				"id": "US", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [],
		arrayItem: true,
		country: 'Multiple',
		listCountry: 'Countries: 50+, Pakistan, United Kingdom, Nigeria, United States, Italy',
		protest_name: '<span class="protest">Worldwide Climate Protest</span>',
		date: '2023/09/15',
		date_text: "September 2023",
		objective: 'To demand an end to oil and gas and for richer nations to invest more in low-carbon energy. ',
		size: 1000001,
		size_text: '>1,000,000',
		participants: '',
		outcomes: '',
		country_text: "Countries: 50+, Pakistan, United Kingdom, Nigeria, United States, Italy",
		civilSociety: true,
		large: true,
		proClimate: true,
		mapData: [],
		inactiveMapData: []
	},
	{
		id: [
			{id: "NL"}
		],
		arrayItem: true,
		country: 'Netherlands',
		listCountry: 'Netherlands',
		protest_name: '<span class="protest">Anti-Fossil Fuel Subsidies Protest</span>',
		date: '2023/09/10',
		date_text: "September 2023",
		objective: 'To demand that the Dutch government stop the use of fossil fuel subsidies; Dutch police detained 2,400 activists. ',
		size: 10001,
		size_text: '>10,000',
		participants: 'Extinction Rebellion',
		outcomes: 'Dutch police detained over 2,400 activists.',
		country_text: "",
		significant: true,
		civilSociety: true,
		large: true,
		antiClimate: true,
		mapData: [
			{
				"id": "NL", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
			{
				"id": "NL", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "KE"}
		],
		arrayItem: true,
		country: 'Kenya',
		listCountry: 'Kenya',
		protest_name: '<span class="protest">FundOurFuture Campaign</span>',
		date: '2023/09/05',
		date_text: "September 2023",
		objective: 'To demand less talk and more progress at the Africa Climate Summit in Nairobi.',
		size: 101,
		size_text: '>100',
		participants: 'ActionAid',
		outcomes: '',
		country_text: "",
		youth: true,
		indigenous: true,
		civilSociety: true,
		proClimate: true,
		mapData: [
			{
				"id": "KE", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
			{
				"id": "KE", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "KR"}
		],
		arrayItem: true,
		country: 'South Korea',
		listCountry: 'South Korea',
		protest_name: '<span class="protest">BTS Coal-Plant Protest</span>',
		date: '2023/08/02',
		date_text: "August 2023",
		objective: 'To protest the construction of coal-plant near Maengbang, “Butter Beach,” popularized by hit BTS 2021 song “Butter.”',
		size: 1001,
		size_text: '>1000',
		participants: 'Kpop4planet, Korea Beyond Coal',
		outcomes: 'Protests did not work. The Samcheok Blue Power began operating in May 2024.',
		country_text: "",
		youth: true,
		civilSociety: true,
		significant: true,
		proClimate: true,
		mapData: [
			{
				"id": "KR", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
			{
				"id": "KR", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "NL"}
		],
		arrayItem: true,
		country: 'Netherlands',
		listCountry: 'Netherlands',
		protest_name: '<span class="protest">Tata Steel plant protest</span>',
		date: '2023/06/25',
		date_text: "June 2023",
		objective: 'To call upon Tata Steel to stop air and soil pollution in the surrounding area. Greenpeace activists were joined by local residents who say the Indian company’s facility in the coastal city is responsible for high levels of heavy metals in nearby soils.  ',
		size: 1001,
		size_text: '>1,000',
		participants: 'Greenpeace',
		outcomes: '',
		country_text: "",
		civilSociety: true,
		mapData: [
			{
				"id": "NL", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
			{
				"id": "NL", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "NL"}
		],
		arrayItem: true,
		country: 'Netherlands',
		listCountry: 'Netherlands',
		protest_name: '<span class="protest">Extinction Rebellion Hague protest</span>',
		date: '2023/05/28',
		date_text: "May 2023",
		objective: 'To oppose government subsidies to the fossil fuel industry, and to encourage government support of Dutch peoples’ desire for climate action. ',
		size: 7001,
		size_text: '>7,000',
		participants: 'Extinction Rebellion, Game of Thrones actor Carice van Houten',
		outcomes: '1,579 protesters arrested',
		country_text: "",
		youth: true,
		civilSociety: true,
		mapData: [
			{
				"id": "NL", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
			{
				"id": "NL", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "DE"}
		],
		arrayItem: true,
		country: 'Germany',
		listCountry: 'Germany',
		protest_name: '<span class="protest">Borkum Anti-Gas Drilling in the North Sea Protest</span>',
		date: '2023/06/18',
		date_text: "June 2023",
		objective: 'To oppose gas drilling in the North Sea, Greenpeace activists demonstrated on Borkum; standing across the beach, protesters formed a human oil rig and then transformed it into a human sun. ',
		size: 400,
		size_text: '400',
		participants: 'Greenpeace',
		outcomes: '',
		country_text: "",
		proClimate: true,
		mapData: [
			{
				"id": "DE", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
			{
				"id": "DE", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "US"}
		],
		arrayItem: true,
		country: 'United States',
		listCountry: 'United States',
		protest_name: '<span class="protest">Amazon Workers’ Climate Protest</span>',
		date: '2023/05/31',
		date_text: "May 2023",
		objective: 'To call Amazon to more aggressively pursue its climate goals and commit to zero emissions by 2030. Protest held in response to Amazon’s corporate stakeholder meeting. ',
		size: 1901,
		size_text: '>1,900',
		participants: '',
		outcomes: '',
		country_text: "",
		labor: true,
		proClimate: true,
		mapData: [
			{
				"id": "US", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
			{
				"id": "US", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "GB"}
		],
		arrayItem: true,
		country: 'United Kingdom',
		listCountry: 'United Kingdom',
		protest_name: '<span class="protest">"The Big One" Earth Day Weekend</span>',
		date: '2023/04/21',
		date_text: "April 2023",
		objective: 'To issue two demands of the UK government on Earth Day: 1) all licenses, approvals, and funding for fossil fuel projects must end and 2) there must be the creation of a Citizen Climate Assembly; organized by Extinction Rebellion.',
		size: 60001,
		size_text: '>60,000 ',
		participants: 'Extinction Rebellion, Greenpeace, Friends of the Earth, PCS Union, Stop Ecocide International, War on Want, 200+',
		outcomes: '',
		country_text: "",
		youth: true,
		labor: true,
		indigenous: true,
		large: true,
		proClimate: true,
		mapData: [
			{
				"id": "NL", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
			{
				"id": "NL", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "CA"},
			{id: "FR"},
			{id: "DE"},
			{id: "IT"},
			{id: "SE"},
			{id: "GB"},
			{id: "US"},
		],
		arrayItem: true,
		country: 'Multiple',
		listCountry: 'Canada, France, Germany, Italy, Sweden, United Kingdom, United States',
		protest_name: '<span class="protest">Earth Day protests</span>',
		date: '2023/04/21',
		date_text: "April 2023",
		objective: 'To call for stronger action on climate change, and decrease reliance on fossil fuels',
		size: 30001,
		size_text: '>30,000',
		participants: '200+ organizations: Extinction Rebellion, Fridays for Future, Greenpeace, Just Stop Oil, Avaaz, Friends of the Earth, Keep Britain Tidy, Ultima Generazione, Les Soulèvements de la Terre',
		outcomes: '',
		country_text: "Countries included: Canada, France, Germany, Italy, Sweden, United Kingdom, United States",
		youth: true,
		labor: true,
		civilSociety: true,
		large: true,
		mapData: [
			{
				"id": "CA", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "FR", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "DE", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "IT", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "SE", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "GB", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "US", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
			{
				"id": "CA", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "FR", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "DE", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "IT", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "SE", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "GB", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "US", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "DE"}
		],
		arrayItem: true,
		country: 'Germany',
		listCountry: 'Germany',
		protest_name: '<span class="protest">Last Generation Highway Protest</span>',
		date: '2023/04/18',
		date_text: "April 2023",
		objective: 'To pressure the German government to enact stricter climate policies as it hosts the Petersberg Climate Dialogue where it will push for other countries to do more to curb planet-warming emissions even as it faces criticism at home for failing to do enough; 200 people detained from over 35 protest locations.',
		size: 201,
		size_text: '>200',
		participants: 'Last Generation',
		outcomes: 'German Chancellor Olaf Scholz suggested that the goal could be a tripling of newly installed solar and wind power by 2030, echoing a target proposed recently by the International Renewable Energy Agency.',
		country_text: "",
		significant: true,
		civilSociety: true,
		proClimate: true,
		mapData: [
			{
				"id": "DE", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "DE", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "US"}
		],
		arrayItem: true,
		country: 'United States',
		listCountry: 'United States',
		protest_name: '<span class="protest">Senior Citizens’ Finance Protest</span>',
		date: '2023/03/23',
		date_text: "March 2023",
		objective: 'To pressure banks to stop financing fossil fuels and heed warnings from leading scientists about the need to rapidly phase out oil, gas and coal to avert the worst effects of climate change.',
		size: 101,
		size_text: '>100',
		participants: 'Third Act',
		outcomes: '',
		country_text: "",
		civilSociety: true,
		proClimate: true,
		mapData: [
			{
				"id": "US", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "US", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "NL"}
		],
		arrayItem: true,
		country: 'Netherlands',
		listCountry: 'Netherlands',
		protest_name: '<span class="protest">Dutch Farmers Protest</span>',
		date: '2023/03/23',
		date_text: "March 2023",
		objective: 'To oppose government plans to limit nitrogen emissions, a policy they say will spell the end of many farms and hit food production.',
		size: 10001,
		size_text: '>10,000',
		participants: 'The BBB or BoerBurgerBeweging (Farmer-Citizen Movement)',
		outcomes: 'BBB’s &ldquo;meteoric rise&rdquo;: BBB won 15 of a total of 75 seats in the Senate, which has the power to block legislation agreed upon in the Lower House of parliament',
		country_text: "",
		significant: true,
		labor: true,
		parties: true,
		large: true,
		antiClimate: true,
		mapData: [
			{
				"id": "NL", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "NL", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "AT"},
			{id: "DE"},
			{id: "IE"},
			{id: "IT"},
			{id: "NZ"},
			{id: "UG"},
			{id: "PK"},
			{id: "NG"}
		],
		arrayItem: true,
		country: 'Multiple',
		listCountry: 'Austria, Germany, Ireland, Italy, New Zealand, Uganda, Pakisan, Nigeria',
		protest_name: '<span class="protest">Global Climate Strike Protest</span>',
		date: '2023/03/23',
		date_text: "March 2023",
		objective: 'To call for stronger action on climate change, decrease reliance on fossil fuels; demonstrations occurred in at least 54 countries.',
		size: 1000001,
		size_text: '>1,000,000',
		participants: 'Fridays for Future, School Strike for Climate,  Free Democrats (German political party -- Germany only)',
		outcomes: '',
		country_text: "Countries included: Austria, Germany, Ireland, Italy, New Zealand, Uganda, Pakistan, Nigeria, 54+",
		youth: true,
		parties: true,
		large: true,
		proClimate: true,
		mapData: [
			{
				"id": "AT", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "DE", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "IE", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "IE", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "NZ", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "UG", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "PK", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "NG", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
			{
				"id": "AT", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "DE", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "IE", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "IT", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "NZ", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "UG", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "PK", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "NG", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "AT"}
		],
		arrayItem: true,
		country: 'Austria',
		listCountry: 'Austria',
		protest_name: '<span class="protest">European Gas Conference Protest</span>',
		date: '2023/03/23',
		date_text: "March 2023",
		objective: 'To oppose alleged environmental destruction and human rights violations, and to call for no new natural gas or oil projects.',
		size: 7001,
		size_text: '>7,000',
		participants: 'Block Gas Coalition, Fridays for Future, Greenpeace',
		outcomes: '',
		country_text: "",
		civilSociety: true,
		youth: true,
		proClimate: true,
		mapData: [
			{
				"id": "AT", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "AT", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "NL"}
		],
		arrayItem: true,
		country: 'The Netherlands',
		listCountry: 'The Netherlands',
		protest_name: '<span class="protest">Utrechtsebaan Blockade Protest</span>',
		date: '2023/01/28',
		date_text: "January 2023",
		objective: 'To oppose the government’s handling of subsidies for fossil energy producers.',
		size: 1001,
		size_text: '>1,000',
		participants: 'Extinction Rebellion',
		outcomes: '',
		country_text: "",
		youth: true,
		proClimate: true,
		mapData: [
			{
				"id": "NL", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "NL", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "CH"}
		],
		arrayItem: true,
		country: 'Switzerland',
		listCountry: 'Switzerland',
		protest_name: '<span class="protest">World Economic Forum 2023 protest</span>',
		date: '2023/01/16',
		date_text: "January 2023",
		objective: 'To demand that concrete climate policies emerge from the WEF and to change the global economy from a profit-minded system.',
		size: 100,
		size_text: '100',
		participants: 'Strike-WEF Collective, Young Socialists of Switzerland',
		outcomes: '',
		country_text: "",
		youth: true,
		proClimate: true,
		mapData: [
			{
				"id": "CH", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "CH", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "DE"}
		],
		arrayItem: true,
		country: 'Germany',
		listCountry: 'Germany',
		protest_name: '<span class="protest">Fridays for Future Protest Ahead of Berlin Election</span>',
		date: '2023/01/13',
		date_text: "January 2023",
		objective: 'To call for stronger action on climate change and to decrease reliance on fossil fuels.',
		size: 4001,
		size_text: '>4,000',
		participants: 'Fridays for Future',
		outcomes: '',
		country_text: "",
		youth: true,
		proClimate: true,
		mapData: [
			{
				"id": "DE", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "DE", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "GB"}
		],
		arrayItem: true,
		country: 'United Kingdom',
		listCountry: 'United Kingdom',
		protest_name: '<span class="protest">Barclays Climate Protest</span>',
		date: '2022/11/01',
		date_text: "November 2022",
		objective: 'To demand that Barclays stop providing financing for fossil fuel projects. ',
		size: 101,
		size_text: '>100',
		participants: 'Extinction Rebellion',
		outcomes: '',
		country_text: "",
		youth: true,
		civilSociety: true,
		proClimate: true,
		mapData: [
			{
				"id": "GB", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "GB", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "PT"}
		],
		arrayItem: true,
		country: 'Portugal',
		listCountry: 'Portugal',
		protest_name: '<span class="protest">Climate Crisis Protest</span>',
		date: '2022/11/12',
		date_text: "November 2022",
		objective: 'To call on economy minister and former oil executive Antonio Costa e Silva to resign and to demand stronger climate action from both the Portuguese government and delegates at COP27.',
		size: 101,
		size_text: '>100',
		participants: '',
		outcomes: '',
		country_text: "",
		proClimate: true,
		mapData: [
			{
				"id": "PT", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "PT", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "EG"}
		],
		arrayItem: true,
		country: 'Egypt',
		listCountry: 'Egypt',
		protest_name: '<span class="protest">COP27 Protest</span>',
		date: '2022/11/12',
		date_text: "November 2022",
		objective: 'To call on wealthier, industrialized nations to compensate lower-income countries heavily affected by climate change.',
		size: 101,
		size_text: '>100',
		participants: 'Pan African Climate Justice Alliance',
		outcomes: 'Delegates at COP27 came to an agreement on a "loss and damage fund" for vulnerable countries.',
		country_text: "",
		significant: true,
		youth: true,
		proClimate: true,
		mapData: [
			{
				"id": "EG", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "EG", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "IL"}
		],
		arrayItem: true,
		country: 'Israel',
		listCountry: 'Israel',
		protest_name: '<span class="protest">Climate Inaction Protest</span>',
		date: '2022/10/28',
		date_text: "October 2022",
		objective: 'To call on the government to take stronger action on climate change and adopt an improved climate bill. ',
		size: 15001,
		size_text: '>15,000',
		participants: 'Society for the Protection of Nature Israel',
		outcomes: '',
		country_text: "",
		youth: true,
		large: true,
		proClimate: true,
		mapData: [
			{
				"id": "IL", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "IL", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "NZ"}
		],
		arrayItem: true,
		country: 'New Zealand',
		listCountry: 'New Zealand',
		protest_name: '<span class="protest">Farm Animal Emissions Tax Protest</span>',
		date: '2022/10/20',
		date_text: "October 2022",
		objective: 'To demand that the New Zealand government drop its plan to adopt a tax on agricultural gases and biogenic methane.',
		size: 1001,
		size_text: '>1,000',
		participants: 'Farm and Agricultural Workers',
		outcomes: 'In 2022, then-Prime Minister Jacinda Ardern announced an agricultural emissions tax. This was highly unpopular then. Now, Prime Minister Christopher Luxon plans to scrap plans for the “burp tax”, which was set to begin in 2025.',
		country_text: "",
		labor: true,
		antiClimate: true,
		mapData: [
			{
				"id": "NZ", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "NZ", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "FR"}
		],
		arrayItem: true,
		country: 'France',
		listCountry: 'France',
		protest_name: '<span class="protest">March Against High Living Costs and Climate Inaction</span>',
		date: '2022/10/16',
		date_text: "October 2022",
		objective: 'To call for increased investment in combatting the climate crisis as well as stronger government action against inflation and rising prices, especially in the energy sector.',
		size: 140001,
		size_text: '>140,000',
		participants: 'France Unbowed Party',
		outcomes: '',
		country_text: "",
		parties: true,
		large: true,
		proClimate: true,
		mapData: [
			{
				"id": "FR", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "FR", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "GB"}
		],
		arrayItem: true,
		country: 'United Kingdom',
		listCountry: 'United Kingdom',
		protest_name: '<span class="protest">Cost of Living and Climate Protest</span>',
		date: '2022/10/01',
		date_text: "October 2022",
		objective: 'To call on the government to take stronger action against rising costs of living and climate change.',
		size: 5001,
		size_text: '>5,000',
		participants: 'Extinction Rebellion, Just Stop Oil',
		outcomes: '',
		country_text: "",
		youth: true,
		civilSociety: true,
		proClimate: true,
		mapData: [
			{
				"id": "GB", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "GB", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "AT"},
			{id: "CA"},
			{id: "CL"},
			{id: "CD"},
			{id: "DE"},
			{id: "ID"},
			{id: "IN"},
			{id: "IT"},
			{id: "JP"},
			{id: "MX"},
			{id: "NZ"},
			{id: "KR"},
			{id: "ES"}
		],
		arrayItem: true,
		country: 'Multiple',
		listCountry: 'Austria, Canada, Chile, Democratic Republic of the Congo, Germany, Indonesia, India, Italy, Japan, Mexico, New Zealand, South Korea, Spain',
		protest_name: '<span class="protest">Global Climate Strike Protest</span>',
		date: '2022/09/24',
		date_text: "September 2022",
		objective: 'To call for greater action in response to the climate crisis and to call for increased aid to countries suffering disproportionate effects.',
		size: 30001,
		size_text: '>30,000',
		participants: 'Fridays for Future',
		outcomes: '',
		country_text: "Countries included: Austria, Canada, Chile, Democratic Republic of the Congo, Germany, Indonesia, India, Italy, Japan, Mexico, New Zealand, South Korea, Spain",
		youth: true,
		large: true,
		proClimate: true,
		mapData: [
			{
				"id": "AT", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "CA", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "CL", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "CD", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "DE", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "ID", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "IN", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "IT", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "JP", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "MX", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "NZ", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "KR", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "ES", 
				settings: { fill: trackedCountry },
			},
		],
		inactiveMapData: [
			{
				"id": "AT", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "CA", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "CL", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "CD", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "DE", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "ID", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "IN", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "IT", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "JP", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "MX", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "NZ", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "KR", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "ES", 
				settings: { fill: inactiveCountry },
			},
		]
	},
	{
		id: [
			{id: "CZ"}
		],
		arrayItem: true,
		country: 'Czech Republic',
		listCountry: 'Czech Republic',
		protest_name: '<span class="protest">Czech Anti-Government Protest</span>',
		date: '2022/09/03',
		date_text: "September 2022",
		objective: 'To demand the resignation of the government of Prime Minister Petr Fiala and to object to the government’s adherence to EU plans to reduce gas consumption and greenhouse emissions.',
		size: 27001,
		size_text: '>70,000',
		participants: '',
		outcomes: '',
		country_text: "",
		parties: true,
		large: true,
		proClimate: true,
		mapData: [
			{
				"id": "CZ", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "CZ", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "HU"}
		],
		arrayItem: true,
		country: 'Hungary',
		listCountry: 'Hungary',
		protest_name: '<span class="protest">Logging Rule Protest</span>',
		date: '2022/08/01',
		date_text: "August 2022",
		objective: 'To call on the Hungarian government to drop plans to ease logging rules amid rising energy prices. ',
		size: 1001,
		size_text: '>1,000',
		participants: '',
		outcomes: '',
		country_text: "",
		antiClimate: true,
		mapData: [
			{
				"id": "HU", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "HU", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "DE"}
		],
		arrayItem: true,
		country: 'Germany',
		listCountry: 'Germany',
		protest_name: '<span class="protest">Garmisch-Partenkirchen G7 Climate Protest</span>',
		date: '2022/06/26',
		date_text: "June 2022",
		objective: 'To call on G7 leaders to take more aggressive action on climate change, poverty, and combatting world hunger.',
		size: 271,
		size_text: '>270',
		participants: 'Oxfam',
		outcomes: '',
		country_text: "",
		proClimate: true,
		youth: true,
		mapData: [
			{
				"id": "DE", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "DE", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "DE"}
		],
		arrayItem: true,
		country: 'Germany',
		listCountry: 'Germany',
		protest_name: '<span class="protest">Munich G7 Climate Protest</span>',
		date: '2022/06/25',
		date_text: "June 2022",
		objective: 'To call on G7 leaders to take more aggressive action on climate change and combating world hunger.',
		size: 4001,
		size_text: '>4,000',
		participants: 'Greenpeace',
		outcomes: '',
		country_text: "",
		proClimate: true,
		youth: true,
		mapData: [
			{
				"id": "DE", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "DE", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "NL"}
		],
		arrayItem: true,
		country: 'The Netherlands',
		listCountry: 'The Netherlands',
		protest_name: '<span class="protest">Dutch Farmer Protest</span>',
		date: '2022/06/01',
		date_text: "June 2022<br />to July 2022",
		objective: 'To demand that the Dutch government drop its plans to adopt strict restrictions on emissions of nitrogen and ammonia, which could force many farms to reduce livestock holdings. ',
		size: 1001,
		size_text: '>1,000',
		participants: 'Farm and Agricultural Workers',
		outcomes: '',
		country_text: "",
		labor: true,
		antiClimate: true,
		mapData: [
			{
				"id": "NL", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "NL", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "US"}
		],
		arrayItem: true,
		country: 'United States',
		listCountry: 'United States',
		protest_name: '<span class="protest">Fight for Our Future Protest</span>',
		date: '2022/04/23',
		date_text: "April 2022",
		objective: 'To urge Congress and the Biden administration to fulfill their campaign pledges to enact comprehensive climate legislation.',
		size: 101,
		size_text: '>100',
		participants: '',
		outcomes: 'Congress passed the Inflation Reduction Act, which featured over $300 billion in climate spending, in August 2022. President Biden signed it into law shortly thereafter.',
		country_text: "",
		significant: true,
		youth: true,
		labor: true,
		civilSociety: true,
		proClimate: true,
		mapData: [
			{
				"id": "US", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "US", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "DE"}
		],
		arrayItem: true,
		country: 'Germany',
		listCountry: 'Germany',
		protest_name: '<span class="protest">Coal Mine Expansion Protest</span>',
		date: '2022/04/01',
		date_text: "April 2022 through January 2023",
		objective: 'To oppose the demolition of a village that will make way for the expansion of a coal mine in northwest Germany.',
		size: 15001,
		size_text: '>15,000',
		participants: 'BUND, Greenpeace, Greta Thunberg, and Friday for Future',
		outcomes: 'Protests did not work. Demolition of the village Luetzerath began in mid-January 2023.',
		country_text: "",
		youth: true,
		parties: true,
		civilSociety: true,
		large: true,
		significant: true,
		proClimate: true,
		mapData: [
			{
				"id": "DE", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "DE", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "GB"}
		],
		arrayItem: true,
		country: 'United Kingdom',
		listCountry: 'United Kingdom',
		protest_name: '<span class="protest">Just Stop Oil Protest</span>',
		date: '2022/04/01',
		date_text: "April 2022",
		objective: 'To demand that the UK government agree to halt new fossil fuel projects in the country.',
		size: 101,
		size_text: '>100',
		participants: 'Just Stop Oil',
		outcomes: '',
		country_text: "",
		civilSociety: true,
		youth: true,
		proClimate: true,
		mapData: [
			{
				"id": "GB", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "GB", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "AR"},
			{id: "AU"},
			{id: "CD"},
			{id: "CH"},
			{id: "CL"},
			{id: "CO"},
			{id: "DE"},
			{id: "DK"},
			{id: "EC"},
			{id: "ES"},
			{id: "FR"},
			{id: "IL"},
			{id: "IT"},
			{id: "MX"},
			{id: "MW"},
			{id: "NG"},
			{id: "NL"},
			{id: "NO"},
			{id: "PA"},
			{id: "PT"},
			{id: "RW"},
			{id: "SE"},
			{id: "SL"},
			{id: "TZ"},
			{id: "UG"},
			{id: "US"},
			{id: "ZW"}
		],
		arrayItem: true,
		country: 'Multiple',
		listCountry: 'Spain, Denmark, the Netherlands, Germany, Switzerland, Italy, Ecuador, Sierra Leone, United States, Nigeria, Democratic Republic of Congo, Australia, Colombia, Mexico, Argentina, Sweden, Norway, Portugal, Uganda, Panama, Zimbabwe, Rwanda, France, Malawi, Tanzania, Chile, Israel',
		protest_name: '<span class="protest">Scientist Rebellion Protest</span>',
		date: '2022/04/01',
		date_text: "April 2022",
		objective: 'To demand greater public attention to climate science and that radical action be taken to address climate change; students, professors, and climate researchers marched.',
		size: 1001,
		size_text: '>1,000',
		participants: '',
		outcomes: '',
		country_text: "Countries included: Argentina, Australia, Chile, Colombia, Denmark, the DRC, Ecuador, France, Germany, Italy, Israel, Malawi, Mexico, Netherlands, Nigeria, Norway,  Panama, Portugal, Rwanda, Sierra Leone, Spain, Sweden, Switzerland, Tanzania,  Uganda, the United States, Zimbabwe",
		youth: true,
		labor: true,
		proClimate: true,
		mapData: [
			{
				"id": "AR", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "AU", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "CD", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "CH", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "CL", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "CO", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "DE", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "DK", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "EC", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "ES", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "FR", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "IL", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "IT", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "MX", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "MW", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "NG", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "NL", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "NO", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "PA", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "PT", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "RW", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "SE", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "SL", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "TZ", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "UG", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "US", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "ZW", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
			
			{
				"id": "AR", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "AU", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "CD", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "CH", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "CL", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "CO", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "DE", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "DK", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "EC", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "ES", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "FR", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "IL", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "IT", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "MX", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "MW", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "NG", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "NL", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "NO", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "PA", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "PT", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "RW", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "SE", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "SL", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "TZ", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "UG", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "US", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "ZW", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "FR"}
		],
		arrayItem: true,
		country: 'France',
		listCountry: 'France',
		protest_name: '<span class="protest">Candidate Platform Protest</span>',
		date: '2022/04/01',
		date_text: "April 2022",
		objective: 'To decry the lack of ambition in presidential candidates’ climate platforms.',
		size: 101,
		size_text: '>100',
		participants: 'Extinction Rebellion',
		outcomes: '',
		country_text: "",
		civilSociety: true,
		proClimate: true,
		mapData: [
				{
				"id": "FR", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "FR", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "AU"},
			{id: "BE"},
			{id: "DE"},
			{id: "IN"},
			{id: "KR"},
			{id: "SE"},
			{id: "TH"},
			{id: "US"},
			{id: "PH"}
		],
		arrayItem: true,
		country: 'Multiple',
		listCountry: 'Thailand, Sweden, Germany, Belgium, United States, India, Australia, South Korea, Philippines',
		protest_name: '<span class="protest">Earth Day Protests</span>',
		date: '2022/04/01',
		date_text: "April 2022",
		objective: 'To call for stronger action on climate change, including by decreasing reliance on fossil fuels and reducing consumption of Russian oil and gas.',
		size: 10001,
		size_text: '>10,000',
		participants: 'Extinction Rebellion, PETA',
		outcomes: '',
		country_text: "Countries included: Australia, Belgium, Germany, India, South Korea, Sweden, Thailand, the United States, Philippines",
		youth: true,
		civilSociety: true,
		proClimate: true,
		mapData: [
			{
				"id": "AU", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "BE", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "DE", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "IN", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "KR", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "SE", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "TH", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "US", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "PH", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
			{
				"id": "AU", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "BE", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "DE", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "IN", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "KR", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "SE", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "TH", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "US", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "PH", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "AT"},
			{id: "CA"},
			{id: "CD"},
			{id: "CL"},
			{id: "DE"},
			{id: "ES"},
			{id: "ID"},
			{id: "IN"},
			{id: "IT"},
			{id: "JP"},
			{id: "KR"},
			{id: "MX"},
			{id: "NZ"}
		],
		arrayItem: true,
		country: 'Multiple',
		listCountry: 'New Zealand, Japan, Germany, the Democratic Republic of the Congo, Canada, Italy, Indonesia, Chile, India, Mexico, South Korea, Spain, Austria',
		protest_name: '<span class="protest">Global climate strike</span>',
		date: '2022/03/05',
		date_text: "March 2022<br />September 2022",
		objective: 'To call for greater action in response to the climate crisis and to call for increased aid to countries suffering disproportionate effects.',
		size: 30001,
		size_text: '>30,000',
		participants: 'Youth; supporters of the Fridays for Future movement.',
		outcomes: '',
		country_text: "Countries included: Austria, Canada, Chile, Democratic Republic of the Congo, Germany, Indonesia, India,  Italy, Japan, Mexico, New Zealand, South Korea, Spain",
		youth: true,
		civilSociety: true,
		large: true,
		mapData: [
			{
				"id": "AT", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "CA", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "CD", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "CL", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "DE", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "ES", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "ID", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "IN", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "IT", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "JP", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "KR", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "MX", 
				settings: { fill: trackedCountry },
			},
			{
				"id": "NZ", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
			{
				"id": "AT", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "CA", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "CD", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "CL", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "DE", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "ES", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "ID", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "IN", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "IT", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "JP", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "KR", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "MX", 
				settings: { fill: inactiveCountry },
			},
			{
				"id": "NZ", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "AU"}
		],
		arrayItem: true,
		country: 'Australia',
		listCountry: 'Australia',
		protest_name: '<span class="protest">Youth Climate Protest</span>',
		date: '2022/03/01',
		date_text: "March 2022",
		objective: 'To demand climate action in response to deadly flooding events that killed 20 people.',
		size: 101,
		size_text: '>100',
		participants: '',
		outcomes: '',
		country_text: "",
		youth: true,
		labor: true,
		indigenous: true,
		proClimate: true,
		mapData: [
				{
				"id": "AU", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "AU", 
				settings: { fill: inactiveCountry },
			}
		]
	},
	{
		id: [
			{id: "BR"}
		],
		arrayItem: true,
		country: 'Brazil',
		listCountry: 'Brazil',
		protest_name: '<span class="protest">Stand for the Earth Protest</span>',
		date: '2022/03/01',
		date_text: "March 2022",
		objective: 'To show opposition to a series of environment and climate-related bills under consideration by Brazil’s Congress that would open up mining, loosen environmental restrictions, and increase logging in the Amazon. ',
		size: 1001,
		size_text: '>1,000',
		participants: 'Rapper Emicida, actor Lázaro Ramos, singer Daniela Mercury, musician Caetano Veloso, and National Union of Students',
		outcomes: '',
		country_text: "",
		indigenous: true,
		proClimate: true,
		mapData: [
				{
				"id": "BR", 
				settings: { fill: trackedCountry },
			}
		],
		inactiveMapData: [
				{
				"id": "BR", 
				settings: { fill: inactiveCountry },
			}
		]
	}
];

let mapDataArray = dataArray;

// desktop results
function desktopResults(html, actualData, activeFilter) {
    if (activeFilter !== 'arrayItem') {
        usedDataArray = dataArray;
    } else {
        usedDataArray = dataArray;
    }
    for (let e in usedDataArray) {
        let filterThing = usedDataArray[e].arrayItem;
        if (activeFilter == 'significant') {
            filterThing = usedDataArray[e].significant;
        } else if (activeFilter == 'youth') {
            filterThing = usedDataArray[e].youth;
        } else if (activeFilter == 'labor') {
            filterThing = usedDataArray[e].labor;
        } else if (activeFilter == 'indigenous') {
            filterThing = usedDataArray[e].indigenous;
        } else if (activeFilter == 'parties') {
            filterThing = usedDataArray[e].parties;
        } else if (activeFilter == 'civilSociety') {
            filterThing = usedDataArray[e].civilSociety;
        } else if (activeFilter == 'large') {
            filterThing = usedDataArray[e].large;
        }
        let filterIcons = '<div class="foci">';
        if (usedDataArray[e].youth) {
            filterIcons = filterIcons + '<div class="youth">Youth</div>';
        }
        if (usedDataArray[e].labor) {
            filterIcons = filterIcons + '<div class="labor">Labor</div>';
        }
        if (usedDataArray[e].indigenous) {
            filterIcons = filterIcons + '<div class="indigenous">Indigenous</div>';
        }
        if (usedDataArray[e].parties) {
            filterIcons = filterIcons + '<div class="parties">Political Parties</div>';
        }
        filterIcons = filterIcons + '</div>';
        let rowToggle = '<div class="col col-20">&nbsp;</div>';
        if (actualData[e].country == 'Multiple' || actualData[e].participants !== '') {
            rowToggle = '<a class="ceip-icon col col-20 content-toggle">+<span class="icon-plus-circle"></span></a>';
        };
        let protestClass = '<span class="climate pro"></span>';
        if (actualData[e].antiClimate) {
            protestClass = '<span class="climate anti"><?xml version="1.0" encoding="UTF-8"?><svg id="Layer_1" xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 97.65 97.65"><defs><style>.cls-1{fill:#ec1c2d;}.cls-2{fill:#fff;font-family:Whitney-Black, Whitney;font-size:81.72px;font-weight:800;}</style></defs><circle class="cls-1" cx="48.83" cy="48.83" r="48.83"/><text class="cls-2" transform="translate(12.08 73.88)"><tspan x="0" y="0">—</tspan></text></svg></span>';
        }
        if (filterThing === true) {
            html +=
                '<tr class="primary">' +
                '<td class="cols">' +
                rowToggle +
                '<div class="col col-80">' +
                actualData[e].country +
                '<span class="hidden">' +
                actualData[e].listCountry +
                '</span></div></td>' +
                '<td>' +
					protestClass
					+
                actualData[e].protest_name +
                '</td>' +
                '<td>' +
                actualData[e].date_text +
                '</td>' +
                '<td>' +
                actualData[e].size_text +
                '</td>' +
                '<td>' +
                filterIcons +
                '</td>' +
                '<td>' +
                actualData[e].objective +
                '</td>' +
                '<td>' +
                actualData[e].outcomes +
                '</td>' +
                '</tr>' +
                '<tr class="secondary hidden--new"><td>&nbsp;</td><td>&nbsp;</td><td colspan="8">';
            if (actualData[e].country == 'Multiple') {
                html +=
                    '<div class="countries details"><strong>Countries</strong>' + actualData[e].listCountry + '</div>';
            }
            if (actualData[e].participants !== '') {
                html +=
                    '<div class="participants details"><strong>Key Participants</strong>' +
                    actualData[e].participants +
                    '</div>';
            }
            html += '</td></tr>';
        }
    }
    document.querySelector('#table-results').innerHTML = html;
}

// responsive results
function responsiveResults(html, actualData, activeFilter) {
    if (activeFilter !== 'arrayItem') {
        usedDataArray = dataArray;
    } else {
        usedDataArray = actualData;
    }
    for (let e in usedDataArray) {
        let filterThing = usedDataArray[e].arrayItem;
        if (activeFilter == 'significant') {
            filterThing = usedDataArray[e].significant;
        } else if (activeFilter == 'youth') {
            filterThing = usedDataArray[e].youth;
        } else if (activeFilter == 'labor') {
            filterThing = usedDataArray[e].labor;
        } else if (activeFilter == 'indigenous') {
            filterThing = usedDataArray[e].indigenous;
        } else if (activeFilter == 'parties') {
            filterThing = usedDataArray[e].parties;
        } else if (activeFilter == 'civilSociety') {
            filterThing = usedDataArray[e].civilSociety;
        } else if (activeFilter == 'large') {
            filterThing = usedDataArray[e].large;
        } else {
            filterThing = usedDataArray[e].arrayItem;
        }
        let filterIcons = '<div class="foci">';
        if (usedDataArray[e].youth) {
            filterIcons = filterIcons + '<div class="youth">Youth</div>';
        }
        if (usedDataArray[e].labor) {
            filterIcons = filterIcons + '<div class="labor">Labor</div>';
        }
        if (usedDataArray[e].indigenous) {
            filterIcons = filterIcons + '<div class="indigenous">Indigenous</div>';
        }
        if (usedDataArray[e].parties) {
            filterIcons = filterIcons + '<div class="parties">Political Parties</div>';
        }
        let rowToggle = '<div class="col col-20">&nbsp;</div>';
        if (actualData[e].country == 'Multiple' || actualData[e].participants !== '') {
            rowToggle = '<a class="ceip-icon col col-20 content-toggle">+<span class="icon-plus-circle"></span></a>';
        };
        let protestClass = '<span class="climate pro"></span>';
        if (actualData[e].antiClimate) {
            protestClass = '<span class="climate anti"><?xml version="1.0" encoding="UTF-8"?><svg id="Layer_1" xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 97.65 97.65"><defs><style>.cls-1{fill:#ec1c2d;}.cls-2{fill:#fff;font-family:Whitney-Black, Whitney;font-size:81.72px;font-weight:800;}</style></defs><circle class="cls-1" cx="48.83" cy="48.83" r="48.83"/><text class="cls-2" transform="translate(12.08 73.88)"><tspan x="0" y="0">—</tspan></text></svg></span>';
        }
        filterIcons = filterIcons + '</div>';
        if (filterThing === true) {
            html +=
                '<tr class="primary">' +
                '<td class="cols">' +
                rowToggle +
				'<div class="col col-80">' +
                actualData[e].country +
                '<span class="hidden">' +
                actualData[e].listCountry +
                '</span><span class="protest">' +
				protestClass +
                actualData[e].protest_name +
                '</span></div></td>' +
                '<td>' +
                actualData[e].date_text +
                '</td>' +
                '<td>' +
                actualData[e].size_text +
                '</td>' +
                '<td>' +
                filterIcons +
                '</td>' +
                '</tr>' +
                '<tr class="secondary hidden--new"><td>&nbsp;</td><td colspan="4">';
            if (actualData[e].country == 'Multiple') {
                html +=
                    '<div class="countries details"><strong>Countries</strong>' + actualData[e].listCountry + '</div>';
            }
            if (actualData[e].participants !== '') {
                html +=
                    '<div class="participants details"><strong>Key Participants</strong>' +
                    actualData[e].participants +
                    '</div>';
            }
            html += '<div class="objective details"><strong>Objectives</strong>' + actualData[e].objective + '</div>';
            if (actualData[e].outcomes !== '') {
                html +=
                    '<div class="outcomes details"><strong>Significant Outcomes</strong>' +
                    actualData[e].outcomes +
                    '</div>';
            }
            html += '</td></tr>';
        }
    }
    document.querySelector('#table-results').innerHTML = html;
}

// mobile results
function mobileResults(html, actualData, activeFilter) {
    if (activeFilter !== 'arrayItem') {
        usedDataArray = dataArray;
    } else {
        usedDataArray = actualData;
    }
    for (let e in usedDataArray) {
        let filterThing = usedDataArray[e].arrayItem;
        if (activeFilter == 'significant') {
            filterThing = usedDataArray[e].significant;
        } else if (activeFilter == 'youth') {
            filterThing = usedDataArray[e].youth;
        } else if (activeFilter == 'labor') {
            filterThing = usedDataArray[e].labor;
        } else if (activeFilter == 'indigenous') {
            filterThing = usedDataArray[e].indigenous;
        } else if (activeFilter == 'parties') {
            filterThing = usedDataArray[e].parties;
        } else if (activeFilter == 'civilSociety') {
            filterThing = usedDataArray[e].civilSociety;
        } else if (activeFilter == 'large') {
            filterThing = usedDataArray[e].large;
        } else {
            filterThing = usedDataArray[e].arrayItem;
        }
        let filterIcons = '<div class="foci">';
        if (usedDataArray[e].youth) {
            filterIcons = filterIcons + '<div class="youth">Youth</div>';
        }
        if (usedDataArray[e].labor) {
            filterIcons = filterIcons + '<div class="labor">Labor</div>';
        }
        if (usedDataArray[e].indigenous) {
            filterIcons = filterIcons + '<div class="indigenous">Indigenous</div>';
        }
        if (usedDataArray[e].parties) {
            filterIcons = filterIcons + '<div class="parties">Political Parties</div>';
        }
        filterIcons = filterIcons + '</div>';
        let rowToggle = '<div class="col col-20">&nbsp;</div>';
        if (actualData[e].country == 'Multiple' || actualData[e].participants !== '') {
            rowToggle = '<a class="ceip-icon col col-20 content-toggle">+<span class="icon-plus-circle"></span></a>';
        };
        let protestClass = '<span class="climate pro"></span>';
        if (actualData[e].antiClimate) {
            protestClass = '<span class="climate anti"><?xml version="1.0" encoding="UTF-8"?><svg id="Layer_1" xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 97.65 97.65"><defs><style>.cls-1{fill:#ec1c2d;}.cls-2{fill:#fff;font-family:Whitney-Black, Whitney;font-size:81.72px;font-weight:800;}</style></defs><circle class="cls-1" cx="48.83" cy="48.83" r="48.83"/><text class="cls-2" transform="translate(12.08 73.88)"><tspan x="0" y="0">—</tspan></text></svg></span>';
        }
        if (filterThing === true) {
            html +=
                '<tr class="primary">' +
				'<td class="cols">' +
                rowToggle +
				'<div class="col col-80">' +
                actualData[e].country +
				protestClass +
                actualData[e].protest_name +
                '</div></td>' +
                '<td>' +
                actualData[e].date_text +
                '</td>' +
                '<td>' +
                actualData[e].size_text +
                '</td>' +
                '</tr>' +
                '<tr class="secondary hidden--new"><td>&nbsp;</td><td colspan="3">' +
                filterIcons;
            if (actualData[e].country == 'Multiple') {
                html +=
                    '<div class="countries details"><strong>Countries</strong>' + actualData[e].listCountry + '</div>';
            }
            if (actualData[e].participants !== '') {
                html +=
                    '<div class="participants details"><strong>Key Participants</strong>' +
                    actualData[e].participants +
                    '</div>';
            }
            html += '<div class="objective details"><strong>Objectives</strong>' + actualData[e].objective + '</div>';
            if (actualData[e].outcomes !== '') {
                html +=
                    '<div class="outcomes details"><strong>Significant Outcomes</strong>' +
                    actualData[e].outcomes +
                    '</div>';
            }
            html += '</td></tr>';
        }
    }
    document.querySelector('#table-results').innerHTML = html;
}

function renderResults() {
    let html = '';
    if (responsive) {
        responsiveResults(html, mapDataArray, 'arrayItem');
    } else if (mobile) {
        mobileResults(html, mapDataArray, 'arrayItem');
    } else {
        desktopResults(html, mapDataArray, 'arrayItem');
    }
}

function resetFilter() {
    // updating the table
    let html = '';
    mapDataArray = dataArray;
    if (responsive) {
        responsiveResults(html, mapDataArray, 'arrayItem');
    } else if (mobile) {
        mobileResults(html, mapDataArray, 'arrayItem');
    } else {
        desktopResults(html, mapDataArray, 'arrayItem');
    }
    // creating a new array to replace dataArray for theader sorting purposes
    mapDataArray = dataArray;
}

function changeButtonOnClick(event) {
    const elList = document.querySelectorAll('.pub-type-label');
    elList.forEach((el) => el.classList.remove('active'));
    event.currentTarget.parentElement.classList.add('active');
}

// filter table by covid protest
function motivationFilter(value) {
    // reset data
    mapDataArray = dataArray;
    // updating the table
    let html = '';
    if (responsive) {
        responsiveResults(html, mapDataArray, value);
    } else if (mobile) {
        mobileResults(html, mapDataArray, value);
    } else {
        desktopResults(html, mapDataArray, value);
    }
    // creating a new array to replace dataArray for theader sorting purposes
    if (value == 'youth') {
        const youthDataArray = dataArray.filter((data) => data.youth);
        mapDataArray = youthDataArray;
    } else if (value == 'labor') {
        const laborDataArray = dataArray.filter((data) => data.labor);
        mapDataArray = laborDataArray;
    } else if (value == 'indigenous') {
        const indigeneousDataArray = dataArray.filter((data) => data.indigenous);
        mapDataArray = indigeneousDataArray;
    } else if (value == 'parties') {
        const partiesDataArray = dataArray.filter((data) => data.parties);
        mapDataArray = partiesDataArray;
    } else if (value == 'civilSociety') {
        const civilSocietyDataArray = dataArray.filter((data) => data.civilSociety);
        mapDataArray = civilSocietyDataArray;
    } else if (value == 'large') {
        const largeDataArray = dataArray.filter((data) => data.large);
        mapDataArray = largeDataArray;
    } else if (value == 'significant') {
        const significantDataArray = dataArray.filter((data) => data.significant);
        mapDataArray = significantDataArray;
    }
}

// buttons

function allMotivationInput(event) {
    resetFilter();
    changeButtonOnClick(event);
}

function youthMotivationInput(event) {
    motivationFilter('youth');
    changeButtonOnClick(event);
}

function laborMotivationInput(event) {
    motivationFilter('labor');
    changeButtonOnClick(event);
}

function indigeneousMotivationInput(event) {
    motivationFilter('indigenous');
    changeButtonOnClick(event);
}

function partiesMotivationInput(event) {
    motivationFilter('parties');
    changeButtonOnClick(event);
}

function largeMotivationInput(event) {
    motivationFilter('large');
    changeButtonOnClick(event);
}

function significantMotivationInput(event) {
    motivationFilter('significant');
    changeButtonOnClick(event);
}

// this is for sorting
function sortFunction(event) {
    const attr = event.target.dataset.attr;
    const elList = document.querySelectorAll('#headings th.sortable');
    elList.forEach((el) => {
        el.classList.remove('active');
        el.classList.remove('reverse');
    });
    event.target.classList.add('active');
    if (event.target.dataset.asc) {
        let asc = false;
        event.target.classList.remove('reverse');
    } else {
        let asc = true;
        event.target.classList.add('reverse');
    }

    // clear any previous sort order indicators
    const elList2 = document.querySelectorAll('#headings th');
    elList2.forEach((el) => {
        el.removeAttribute('data-asc');
    });
    // set sort order indicator
    if (asc) {
        event.target.setAttribute('data-asc', 'asc');
    }

    sortResults(attr, asc);
}

function sortResults(prop, asc) {
    mapDataArray.sort(function (a, b) {
        if (asc) {
            return a[prop] > b[prop] ? 1 : a[prop] < b[prop] ? -1 : 0;
        } else {
            return b[prop] > a[prop] ? 1 : b[prop] < a[prop] ? -1 : 0;
        }
    });
    renderResults();
}

// opening rows for the secondary content
function openNextRow(event) {
    event.preventDefault();
    const nextRow = event.target.parentElement.parentElement.nextSibling;

    event.target.classList.toggle('active');
    event.target.parentElement.classList.toggle('open');

    if (nextRow.classList.contains('hidden--new')) {
        nextRow.classList.toggle('hidden--new--active');
    }
}

function aboutTab() {
    document.querySelector('#aboutTabClick').onclick = function (e) {
        document.getElementById('aboutTabContent').classList.add('active');
    };
    document.querySelector('#close-about').onclick = function (e) {
        document.getElementById('aboutTabContent').classList.remove('active');
    };
}
function openTab(tabName, event) {
    let i, tabcontent, tablinks, activeTab;
    tabcontent = document.getElementsByClassName('tab-pane');
    for (i = 0; i < tabcontent.length; i++) {
        tabcontent[i].classList.remove('active');
    }
    document.getElementById(tabName).classList.add('active');

    tablinks = document.getElementsByClassName('tab-clicks');
    for (i = 0; i < tablinks.length; i++) {
        tablinks[i].parentElement.classList.remove('active');
    }
    activeTab = event.target.parentElement;
    activeTab.classList.add('active');
}

function printWindow() {
    window.print();
}

function callMap() {
    // Create root element
    // https://www.amcharts.com/docs/v5/getting-started/#Root_element
    const root = am5.Root.new('chartdiv');

    // Set themes
    // https://www.amcharts.com/docs/v5/concepts/themes/
    root.setThemes([am5themes_Animated.new(root)]);

    // Create the map chart
    // https://www.amcharts.com/docs/v5/charts/map-chart/
    const chart = root.container.children.push(
        am5map.MapChart.new(root, {
            panX: 'none',
            panY: 'none',
            wheelX: 'none',
            wheelY: 'none',
            projection: am5map.geoMercator(),
            homeZoomLevel: 2,
            homeGeoPoint: { longitude: 0, latitude: 20 },
        }),
    );
    // adding the zoom controls and making them teal
    const zoomControl = chart.set('zoomControl', am5map.ZoomControl.new(root, {}));
    const zoomButtons = ['minusButton', 'plusButton'];

    zoomButtons.forEach((button) => {
        zoomControl[button].get('background').setAll({
            fill: am5.color(0x00a79d),
        });
        zoomControl[button]
            .get('background')
            .states.create('hover', {})
            .setAll({
                fill: am5.color(0x99dcd8),
            });
    });

    // Create series for background fill
    // https://www.amcharts.com/docs/v5/charts/map-chart/map-polygon-series/#Background_polygon
    const backgroundSeries = chart.series.push(am5map.MapPolygonSeries.new(root, {}));
    backgroundSeries.mapPolygons.template.setAll({
        fill: am5.color('#ffffff'),
        fillOpacity: 1,
        strokeOpacity: 0,
    });

    // Add background polygon
    // https://www.amcharts.com/docs/v5/charts/map-chart/map-polygon-series/#Background_polygon
    backgroundSeries.data.push({
        geometry: am5map.getGeoRectangle(90, 180, -90, -180),
    });

    // Create main polygon series for countries
    // https://www.amcharts.com/docs/v5/charts/map-chart/map-polygon-series/
    const polygonSeries = chart.series.push(
        am5map.MapPolygonSeries.new(root, {
            geoJSON: am5geodata_worldLow,
            exclude: ['AQ'],
        }),
    );

    //country list country code
    const countryAbbreviations = [
        'AR',
        'AT',
        'AU',
        'BD',
        'BE',
        'BR',
        'CA',
        'CD',
        'CH',
        'CL',
        'CO',
        'CZ',
        'DE',
        'DK',
        'EC',
        'EG',
        'ES',
        'FR',
        'GB',
        'HU',
        'ID',
        'IE',
        'IL',
        'IR',
        'IN',
        'IT',
        'JP',
        'KR',
        'MW',
        'MX',
        'NG',
        'NL',
        'NO',
        'NZ',
        'PA',
        'PK',
        'PH',
        'PL',
        'PT',
		'RS',
        'RW',
        'SE',
        'SL',
        'TH',
        'TZ',
        'UA',
        'UG',
        'US',
        'ZW',
    ];
    const multipleCountryAbbreviations = [
        'AT',
        'AU',
        'BE',
        'CD',
        'CL',
        'DE',
        'ES',
        'FR',
        'GB',
        'IL',
        'IN',
        'IT',
        'KR',
        'MX',
        'NL',
        'NZ',
		'PH',
        'PT',
        'SE',
        'UG',
        'US',
    ];

    // Configure series
    polygonSeries.mapPolygons.template.setAll({
        tooltipText: '{name}',
        interactive: true,
        fill: am5.color('#e6e7ed'),
        templateField: 'settings',
    });

    polygonSeries.events.on('datavalidated', function () {
        chart.goHome();
    });

    polygonSeries.mapPolygons.template.states.create('active', {
        fill: clickedCountry,
    });

    function closeButton() {
        // close button in info
        document.querySelector('#closeInfo').onclick = function (e) {
            document.getElementById('info').classList.remove('active');
            countryAbbreviations.forEach((value) => {
                let countryDataItem = polygonSeries.getDataItemById(value);
                let polygon = countryDataItem.get('mapPolygon');
                polygon.states.apply('default');
            });
            let elList = document.querySelectorAll('#protestTable tr.primary');
            elList.forEach((el) => {
                el.classList.remove('hidden--new');
            });
        };
    }

    polygonSeries.mapPolygons.template.events.on('click', function (ev) {
        let data = ev.target.dataItem.dataContext;
        let info = document.getElementById('info');
        let html = '';
        // only look into the prefiltered version -- we are also using this so certain actions (activating info box, activating country, filtering table) only happen for a 'positive' result
        for (let e in mapDataArray) {
            if (mapDataArray[e].country == data.name) {
                // making info box visible
                document.getElementById('info').classList.add('active');

                //gathering data
                html +=
                    mapDataArray[e].protest_name +
                    '<span class="protest">, ' +
                    mapDataArray[e].date_text +
                    '</span>' +
                    '<div><strong>Objective:</strong> ' +
                    mapDataArray[e].objective +
                    '</div>';

                // change table as well as map
                let countryValue = data.name.toLowerCase();
                let elList = document.querySelectorAll('#protestTable tr.primary');
                elList.forEach((el) => {
                    if (el.firstChild.childNodes[1].innerHTML.toLowerCase().indexOf(countryValue) == -1) {
                        el.classList.add('hidden--new');
                    }
                });

                // making clicked country active coolor
                countryAbbreviations.forEach((value) => {
                    let countryDataItem = polygonSeries.getDataItemById(value);
                    let polygon = countryDataItem.get('mapPolygon');
                    if (value !== data.id) {
                        polygon.states.apply('default');
                    } else {
                        polygon.states.apply('active');
                    }
                });
            }
        }
        info.innerHTML =
            "<div class='close-panel' id='closeInfo'><span class='ceip-icon icon-cancel-circle'></span>X</div>";
        if (html) {
            info.innerHTML +=
                '<div class="description"><div class="title">' +
                data.name +
                '</div><div class="info-text">' +
                html +
                '</div></div>';
        } else {
            info.innerHTML +=
                "<div class='close-panel' id='closeInfo'><span class='ceip-icon icon-cancel-circle'>X</span></div><div class='description'><p><i>No description provided.</i></p></div>";
        }

        // close button in info
        closeButton();
    });

    //reconfigure based on filters

    function buildMapFromButton(inactiveResults, results) {
        // create array that is only the negative results inactiveMapData
        let inactiveMapDataArray = [];
        let index = 0;
        for (let e in inactiveResults) {
            inactiveMapDataArray[index] = inactiveResults[e].inactiveMapData;
            index++;
        }
        // push to new polygonSeries the array with positive results as normal
        let activeMapColors = [];
        for (let e in results) {
            for (let f in results[e].mapData) {
                activeMapColors.push(results[e].mapData[f]);
            }
        }
        for (let e in activeMapColors) {
            activeMapColors[e].settings.fill = filteredCountry;
        }
        // combine the inactive array for light color inactive countries and active array for bright active countries
        const fullMap = inactiveMapDataArray.concat(activeMapColors);
        polygonSeries.data.setAll(fullMap);
    }

    //All protests
    document.querySelector('#radio-all').onclick = function (e) {
        //create array with positive results
        const results = dataArray.filter((data) => data.arrayItem);
        // push to new polygonSeries the array with positive results as normal
        let newMapColors = [];
        for (let e in results) {
            for (let f in results[e].mapData) {
                newMapColors.push(results[e].mapData[f]);
            }
        }
        for (let e in newMapColors) {
            newMapColors[e].settings.fill = trackedCountry;
        }
        polygonSeries.data.setAll(newMapColors);
    };

    //Active protests
    document.querySelector('#radio-youth').onclick = function (e) {
        //create array with positive results
        const results = dataArray.filter((data) => data.youth);
        // create an array of all of the country ID that are in the results
        let resultsIDList = [];
        let index = 0;
        results.forEach((e) => {
            resultsIDList[index] = e.id;
            index++;
        });
        // if positive results array is missing a multiple, we need to add it back in to inactve
        let multipleResultsPositive = [];
        // compare list of countries with multiple results to list of countries in positive results
        multipleCountryAbbreviations.forEach((el) => {
            if (resultsIDList.includes(el)) {
                multipleResultsPositive.push(el);
            }
        });
        let inactiveResults = [];
        // if no countries are in BOTH positive and multiple results, return simple list of inactive countries (negative results)
        if (multipleResultsPositive.length < 1) {
            inactiveResults = dataArray.filter((data) => !data.youth);
        }
        // if any countries are in positive AND multiple, filter them out of the negative results list -- so if a country is in the multiple results positive array, we skip adding them to the inactiveResults array.  We only add an item to inactiveResults if it is not in the multiple positive array AND it is not whatever variable we are testing for
        else {
            dataArray.forEach((e) => {
                if (!multipleResultsPositive.includes(e.id)) {
                    if (!e.youth) {
                        inactiveResults.push(e);
                    }
                }
            });
        }
        buildMapFromButton(inactiveResults, results);
    };
    //Large protests
    document.querySelector('#radio-labor').onclick = function (e) {
        //create array with positive results
        const results = dataArray.filter((data) => data.labor);
        // create an array of all of the country ID that are in the results
        let resultsIDList = [];
        let index = 0;
        results.forEach((e) => {
            resultsIDList[index] = e.id;
            index++;
        });
        // if positive results array is missing a multiple, we need to add it back in to inactve
        let multipleResultsPositive = [];
        // compare list of countries with multiple results to list of countries in positive results
        multipleCountryAbbreviations.forEach((el) => {
            if (resultsIDList.includes(el)) {
                multipleResultsPositive.push(el);
            }
        });
        let inactiveResults = [];
        // if no countries are in BOTH positive and multiple results, return simple list of inactive countries (negative results)
        if (multipleResultsPositive.length < 1) {
            inactiveResults = dataArray.filter((data) => !data.labor);
        }
        // if any countries are in positive AND multiple, filter them out of the negative results list -- so if a country is in the multiple results positive array, we skip adding them to the inactiveResults array.  We only add an item to inactiveResults if it is not in the multiple positive array AND it is not whatever variable we are testing for
        else {
            dataArray.forEach((e) => {
                if (!multipleResultsPositive.includes(e.id)) {
                    if (!e.labor) {
                        inactiveResults.push(e);
                    }
                }
            });
        }
        buildMapFromButton(inactiveResults, results);
    };
    //Violent protests
    document.querySelector('#radio-indigenous').onclick = function (e) {
        //create array with positive results
        const results = dataArray.filter((data) => data.indigenous);
        // create an array of all of the country ID that are in the results
        let resultsIDList = [];
        let index = 0;
        results.forEach((e) => {
            resultsIDList[index] = e.id;
            index++;
        });
        // if positive results array is missing a multiple, we need to add it back in to inactve
        let multipleResultsPositive = [];
        // compare list of countries with multiple results to list of countries in positive results
        multipleCountryAbbreviations.forEach((el) => {
            if (resultsIDList.includes(el)) {
                multipleResultsPositive.push(el);
            }
        });
        let inactiveResults = [];
        // if no countries are in BOTH positive and multiple results, return simple list of inactive countries (negative results)
        if (multipleResultsPositive.length < 1) {
            inactiveResults = dataArray.filter((data) => !data.indigenous);
        }
        // if any countries are in positive AND multiple, filter them out of the negative results list -- so if a country is in the multiple results positive array, we skip adding them to the inactiveResults array.  We only add an item to inactiveResults if it is not in the multiple positive array AND it is not whatever variable we are testing for
        else {
            dataArray.forEach((e) => {
                if (!multipleResultsPositive.includes(e.id)) {
                    if (!e.indigenous) {
                        inactiveResults.push(e);
                    }
                }
            });
        }
        buildMapFromButton(inactiveResults, results);
    };
    //Long protests
    document.querySelector('#radio-parties').onclick = function (e) {
        //create array with positive results
        const results = dataArray.filter((data) => data.parties);
        // create an array of all of the country ID that are in the results
        let resultsIDList = [];
        let index = 0;
        results.forEach((e) => {
            resultsIDList[index] = e.id;
            index++;
        });
        // if positive results array is missing a multiple, we need to add it back in to inactve
        let multipleResultsPositive = [];
        // compare list of countries with multiple results to list of countries in positive results
        multipleCountryAbbreviations.forEach((el) => {
            if (resultsIDList.includes(el)) {
                multipleResultsPositive.push(el);
            }
        });
        let inactiveResults = [];
        // if no countries are in BOTH positive and multiple results, return simple list of inactive countries (negative results)
        if (multipleResultsPositive.length < 1) {
            inactiveResults = dataArray.filter((data) => !data.parties);
        }
        // if any countries are in positive AND multiple, filter them out of the negative results list -- so if a country is in the multiple results positive array, we skip adding them to the inactiveResults array.  We only add an item to inactiveResults if it is not in the multiple positive array AND it is not whatever variable we are testing for
        else {
            dataArray.forEach((e) => {
                if (!multipleResultsPositive.includes(e.id)) {
                    if (!e.parties) {
                        inactiveResults.push(e);
                    }
                }
            });
        }
        buildMapFromButton(inactiveResults, results);
    };
    //Covid protests
    document.querySelector('#radio-large').onclick = function (e) {
        //create array with positive results
        const results = dataArray.filter((data) => data.large);
        // create an array of all of the country ID that are in the results
        let resultsIDList = [];
        let index = 0;
        results.forEach((e) => {
            resultsIDList[index] = e.id;
            index++;
        });
        // if positive results array is missing a multiple, we need to add it back in to inactve
        let multipleResultsPositive = [];
        // compare list of countries with multiple results to list of countries in positive results
        multipleCountryAbbreviations.forEach((el) => {
            if (resultsIDList.includes(el)) {
                multipleResultsPositive.push(el);
            }
        });
        let inactiveResults = [];
        // if no countries are in BOTH positive and multiple results, return simple list of inactive countries (negative results)
        if (multipleResultsPositive.length < 1) {
            inactiveResults = dataArray.filter((data) => !data.large);
        }
        // if any countries are in positive AND multiple, filter them out of the negative results list -- so if a country is in the multiple results positive array, we skip adding them to the inactiveResults array.  We only add an item to inactiveResults if it is not in the multiple positive array AND it is not whatever variable we are testing for
        else {
            dataArray.forEach((e) => {
                if (!multipleResultsPositive.includes(e.id)) {
                    if (!e.large) {
                        inactiveResults.push(e);
                    }
                }
            });
        }
        buildMapFromButton(inactiveResults, results);
    };
    //Covid protests
    document.querySelector('#radio-significant').onclick = function (e) {
        //create array with positive results
        const results = dataArray.filter((data) => data.significant);
        // create an array of all of the country ID that are in the results
        let resultsIDList = [];
        let index = 0;
        results.forEach((e) => {
            resultsIDList[index] = e.id;
            index++;
        });
        // if positive results array is missing a multiple, we need to add it back in to inactve
        let multipleResultsPositive = [];
        // compare list of countries with multiple results to list of countries in positive results
        multipleCountryAbbreviations.forEach((el) => {
            if (resultsIDList.includes(el)) {
                multipleResultsPositive.push(el);
            }
        });
        let inactiveResults = [];
        // if no countries are in BOTH positive and multiple results, return simple list of inactive countries (negative results)
        if (multipleResultsPositive.length < 1) {
            inactiveResults = dataArray.filter((data) => !data.significant);
        }
        // if any countries are in positive AND multiple, filter them out of the negative results list -- so if a country is in the multiple results positive array, we skip adding them to the inactiveResults array.  We only add an item to inactiveResults if it is not in the multiple positive array AND it is not whatever variable we are testing for
        else {
            dataArray.forEach((e) => {
                if (!multipleResultsPositive.includes(e.id)) {
                    if (!e.significant) {
                        inactiveResults.push(e);
                    }
                }
            });
        }
        buildMapFromButton(inactiveResults, results);
    };

    // this needs to happen at the end after all the click events are declared
    let mapColors = [];
    for (let e in mapDataArray) {
        for (let f in mapDataArray[e].mapData) {
            mapColors.push(mapDataArray[e].mapData[f]);
        }
    }
    polygonSeries.data.setAll(mapColors);

    // Make stuff animate on load
    chart.appear(1000, 100);
}
</script>

<style lang="scss" scoped>
/* layout */
$gutter: 30px;
$site-width: 1000px;

$carnegie-blue: #003a63;
$gray-light: #e4e3dd;
$gold: #a28556;
$black: #000;
$white: #fff;
$teal: #00a79d;
$dark-blue: #00365e;
$light-blue: #27aae1;
$dark-gray-new: #6e6e71;
$light-gray: #f5f5f5;
$dark-gray: #929497;
$yellow: #fbb040;
$primary-light-blue: #3183b1;
$primary-dark-blue: #072a42;
$teal-light: #99dcd8;
/* 40% $teal */
$light-blue-light: #a0d9f2;

$roman-normal-book: 'Frutiger Neue LT W06 Book', Arial, sans-serif;
$roman-italic-book: 'Frutiger Neue LT W06 Book It', Arial, sans-serif;
$roman-normal-bold: 'Frutiger Neue LT W06 Bold', 'Arial Black', Gadget, sans-serif;

@mixin all-transition($time) {
    transition: all $time;
    -webkit-transition: all $time;
}
.climate-protest-tracker {
	/*layout basics*/
	.foreground {
		position: relative;
		margin: 0 auto;
		box-sizing: content-box;
		max-width: $site-width;
		padding-left: calc($gutter / 2);
		padding-right: calc($gutter / 2);
	}

	.section {
		display: block;
		clear: both;
		margin-top: $gutter;
		margin-bottom: $gutter;
	}

	:deep(.hidden) {
		display: none;
	}

	:deep(.cols) {
		clear: both;
		/*use a .no-gutters class to undo these default styles */
		margin-left: calc($gutter / 2) * -1;
		margin-right: calc($gutter / 2) * -1;

		&:before,
		&:after {
			content: ' ';
			display: table;
			height: 0;
			clear: both;
			font-size: 1px;
			line-height: 0px;
			visibility: hidden;
		}

		/* @include bp(mobile) { */
		/*     margin-left: 0 !important;*/
		/*     margin-right: 0 !important;*/
		/*}*/
	}

	.col {
		float: left;

		/* @include bp(mobile) {*/
		/*     float: none;*/
		/*     width: 100% !important;*/

		/*     > .gutters {*/
		/*         margin-left: 0;*/
		/*         margin-right: 0;*/
		/*     }*/
		/* }*/
	}

	.col-70 {
		width: 70%;
	}

	/*two across*/
	.col-50 {
		width: 50%;
	}

	.col-30 {
		width: 30%;
	}

	/*four across*/
	.col-25 {
		width: 25%;
	}

	.padded {
		padding: $gutter;
	}

	:deep(.col-20) {
		display: inline-block;
		width: 20%;
	}

	:deep(.col-80) {
		display: inline-block;
		width: 80%;
	}

	:deep(.nav-tabs) {
		div {
			border: 0 !important;
			text-transform: uppercase;
		}

		> li {
			margin-bottom: 0;
			float: left;
			position: relative;
			display: block;

			> div {
				line-height: 1;
				border-radius: 0;
			}
		}
	}

	.gutter-right {
		margin-right: 30px;
	}

	.gutter-left {
		margin-left: 30px;
	}

	.zone-title {
		background-color: $black;
		background-image: url(https://ceipimg.s3.amazonaws.com/static/climateProtestTracker-header.jpg);
		background-position: top center;
		background-repeat: no-repeat;
		text-align: left;

		.container-title {
			padding: 100px 0 250px 4%;
			width: 45%;
		}

		h2 {
			background-color: $teal;
			color: $white;
			display: inline-block;
			font-family: $roman-normal-bold;
			font-size: 3rem;
			margin: $gutter 0;
			padding: calc($gutter / 5) 20px 12px 20px;
		}

		p {
			color: $black;
			font-family: $roman-normal-book;
			padding-left: 20px;

			&.uppercase {
				font-family: $roman-normal-bold;
				margin-top: calc($gutter * 2);
			}

			> strong {
				color: $carnegie-blue;
				display: block;
				font-family: $roman-normal-bold;
				font-size: 0.8rem;
				font-weight: normal;
				margin-top: calc($gutter / 3);
				text-transform: uppercase;
			}
		}
	}

	.results-table {
		margin-top: 50px;
		table {
			margin: calc($gutter / 2);
			width: 95%;

			thead {
				position: sticky;
				position: -webkit-sticky;
				top: 140px;
			}

			td,
			th {
				padding: calc($gutter / 4) calc($gutter / 3);
			}

			th {
				background-color: $white;
				font-family: $roman-normal-bold;
				font-size: 1.3rem;
				font-weight: normal;
				text-transform: uppercase;

				&.sortable {
					color: $dark-blue;
					cursor: pointer;

					&.active {
						&:after {
							border-top: 10px solid $dark-blue;
							border-left: 7px solid transparent;
							border-right: 7px solid transparent;
							content: '';
							display: inline-block;
							margin: 5px 0 0 5px;
							padding-left: 2px;
						}

						&.reverse:after {
							-webkit-transform: rotate(180deg);
							-ms-transform: rotate(180deg);
							transform: rotate(180deg);
						}
					}
				}
			}

			:deep(tr) {
				&:hover,
				&.open,
				&.secondary {
					background-color: $light-gray;
				}
			}

			:deep(.climate) {
				&.anti {
					float: left; margin-left: -1em; margin-top: .3em;
				}
			}

			:deep(.protest-name) {
				min-width: 10%;
			}

			:deep(.participants) {
				min-width: 16%;
			}

			:deep(td) {
				border-bottom: none;
				border-top: 1px solid $gray-light;
				font-family: $roman-normal-book;
				font-size: 1.4rem;
				line-height: 1.3em;
				padding: .8em 1em;

				.protest {
					display: block;
					font-family: $roman-italic-book;
				}

				.foci {
					display: block;

					> div {
						border-radius: 15px;
						color: $white;
						display: inline-block;
						font-size: 1rem;
						margin-bottom: 5px;
						margin-right: 7px;
						padding: 3px 10px 4px 10px;
						background-color: $dark-blue;
					}
				}a.ceip-icon {
					color: $light-blue;
					line-height: 1.4em;

					&:hover {
						text-decoration: none;
					}
				}

				input {
					display: block;
					margin: 0 auto;
				}

				&.cols > div {
					font-family: $roman-normal-bold;
					font-weight: normal;
				}
			}

			:deep(tr.secondary) {
				strong {
					display: block;
					font-family: $roman-normal-bold;
					font-weight: normal;
					margin-bottom: calc($gutter / 6);
				}

				.details {
					padding-bottom: calc($gutter / 2);
					padding-right: $gutter;
				}

				td {
					border-top: none;
				}

				&.hidden--new {
					display: none;
				}

				&.hidden--new--active {
					display: table-row;
				}
			}
		}
	}

	.download-buttons {
		margin: $gutter 0;

		.foreground {
			text-align: center;
		}

		.button {
			background-color: $light-blue-light;
			color: $dark-gray-new;
			cursor: pointer;
			font-size: 1rem;
			margin: 0 calc($gutter / 2);
			padding: calc($gutter / 2);
			text-transform: uppercase;

			&:after {
				content: none;
			}

			&:hover {
				background-color: $light-blue;
				color: $white;
			}
		}
	}

	.filter-area {
		background-color: $white;
		height: 95px;
		padding-top: 15px;
		position: sticky;
		position: -webkit-sticky;
		top: 0;

		.climate-type {
			margin: 0;
			padding: 0 1.3em;
			font-family: $roman-normal-bold;
			font-size: 1.2rem;
			font-weight: normal;
			line-height: 2.1em;
			svg {float: left; margin-right: 1em; margin-top: .3em;}
		}
	}

	.filters {
		background-color: $white;
		display: table;
		font-size: 1rem;
		margin: $gutter auto;
		text-align: center;
		width: 100%;
		strong {
			display: block;
			font-family: $roman-normal-bold;
			font-size: 1.2rem;
			font-weight: normal;
			line-height: 2.1em;
			margin: auto calc($gutter / 3) 0 $gutter;
			text-align: left;
		}

		form {
			margin-bottom: 0;
			padding: 1.3em;

			fieldset {
				padding-left: calc($gutter / 2);

				label {
					background-color: $white;
					border: 2px solid $dark-blue;
					border-radius: 20px;
					cursor: pointer;
					float: left;
					margin: 0 calc($gutter / 2) 0 calc($gutter / 5);
					-moz-outline: 1px solid $black;
					padding: 5px 10px;

					&:first-of-type {
						margin-left: 0;
					}

					input {
						display: none;
					}

					span.name {
						color: $dark-blue;
						font-family: $roman-normal-bold;
						font-size: 1rem;
						text-transform: uppercase;
					}

					&:hover {
						background-color: $dark-blue;

						span.name {
							color: $white;
						}
					}

					&.active {
						background-color: $dark-blue;

						span.name {
							color: $white;
						}

						&:hover {
							background-color: $dark-blue;

							span.name {
								color: $white;
							}
						}
					}
				}
			}

			&.climate-radio-all fieldset label {
				border: 2px solid $teal;

				span.name {
					color: $teal;
				}

				&:hover {
					background-color: $teal;

					span.name {
						color: $white;
					}
				}

				&.active {
					background-color: $teal;

					span.name {
						color: $white;
					}

					&:hover {
						background-color: $teal;

						span.name {
							color: $white;
						}
					}
				}
			}
		}

		.participants-filters,
		.all-filters {
			float: left;
		}

		.other-filters {
			float: right;
		}
	}

	.map {
		background-color: #ffffff;
		margin: $gutter 0 0 0;
		position: relative;

		#aboutTab {
			h3 {
				background-color: $light-blue;
				border-radius: 5px 0 0 5px;
				color: $white;
				cursor: pointer;
				display: none;
				font-family: $roman-normal-bold;
				font-size: 1rem;
				line-height: 2.3em;
				padding: 20px 0;
				position: fixed;
				right: 0;
				top: 40vh;
				writing-mode: vertical-lr;
				text-orientation: mixed;
				text-transform: uppercase;
				z-index: 1;

				&.active {
					display: block;
				}

				span {
					display: inline;
				}
			}

			#aboutTabContent {
				box-shadow: -5px 0px 8px -3px rgba(0, 0, 0, 0.69);
				display: none;
				background-color: $white;
				width: 90vw;
				height: 65vh;
				cursor: auto;
				overflow-y: scroll;
				position: fixed;
				right: 0;
				top: 20vh;
				-webkit-border-radius: 5px 0 0 5px;
				-moz-border-radius: 5px 0 0 5px;
				border-radius: 5px 0 0 5px;
				z-index: 9;

				&.active {display: block;}

				h3 {
					border-radius: 0;
					display: block;
					height: 100%;
					top: 0;
					display: none;
				}

				.section {
					padding: 0 15px 15px 0;

					&.about-overlay {
						&.section {
							margin-top: 0;
							margin-bottom: 0;
						}

						.foreground {
							padding-left: 0;
						}
					}
				}

				.top-section {
					background-color: $white;
					display: block;
					padding: calc($gutter / 2) 0 calc(calc($gutter * 2)) 0;
					position: sticky;
					position: webkit-sticky;
					top: 0;
					width: 100%;
					z-index: 9;

					.foreground {
						float: left;
						position: relative;
						left: calc(calc($gutter * 2));
					}
				}

				.nav {
					margin-left: calc($gutter * 2);
				}

				.nav-tabs {
					border-bottom: none;
					width: 100%;

					div {
						background: none;
						color: $light-blue-light !important;
						font-family: $roman-normal-bold;
						font-size: 1.3rem;
						padding-left: 0;
						padding-right: calc($gutter * 1.5);

						&:hover {
							color: $light-blue !important;
						}
					}

					li.active div {
						color: $light-blue !important;
					}

					li {
						&:last-child {
							div {
								padding-right: 0;
							}
						}
					}
				}

				.tab-content {
					font-size: 1.2rem;
					line-height: normal;

					.component {
						margin: 0 !important;

						.cols {
							margin-left: 0;
							margin-right: 0;
						}
					}

					.tab-pane {
						margin: $gutter 0 0 0;
						display: none;

						&.active {display: block;}

						h4 {
							color: $dark-blue;
							font-family: $roman-normal-bold;
							font-size: 0.9rem;
							margin-bottom: 4em;
							text-transform: uppercase;
						}

						p,
						li,
						.col {
							font-family: $roman-normal-book;
						}

						p {
							margin-bottom: $gutter;
						}

						em {
							font-family: $roman-italic-book;
						}

						strong {
							display: block;
							font-family: $roman-normal-bold;
							font-weight: normal;
							padding-bottom: calc($gutter / 3);
						}

						li.gutter-bottom {
							margin-bottom: calc($gutter / 2);
						}

						.button {
							background-color: $light-blue;
							color: $white;
							cursor: pointer;
							font-size: 1rem;
							margin: 0 calc($gutter / 2) 0 0;
							padding: calc($gutter / 2);
							text-transform: uppercase;

							&:after {
								content: none;
							}

							&:hover {
								background-color: $light-blue;
								color: $white;
							}
						}
					}
				}

				.close-button {
					opacity: 1;

					button.close-panel {
						background-color: transparent;
						color: $light-blue;
						margin-bottom: $gutter;
						font-family: $roman-normal-bold;
						font-size: 2rem;
						font-weight: normal;
						padding: 0;
						position: absolute;
						left: 20px;
						top: 20px;
						text-transform: uppercase;

						&:after {
							border: none;
						}
					}
				}
			}
		}
	}

	#countrySelect {
		position: absolute;
		left: 6%;
		top: 10px;
	}

	:deep(#info) {
		display: none;

		&.active {
			background-color: rgba(255, 255, 255, 0.9);
			border-radius: 0;
			box-shadow: -5px 0px 8px -3px rgba(0, 0, 0, 0.69);
			position: absolute;
			display: block;
			width: 20%;
			max-height: 300px;
			top: 45px;
			left: 45px;

			.description {
				font-family: $roman-normal-book;
				font-size: 0.9rem;
				overflow-y: scroll;
				padding: 0 15px 0 15px;
				max-height: 280px;

				.title {
					background-color: $white;
					font-family: $roman-normal-bold;
					font-size: 1.2rem;
					margin: 0 calc($gutter / -2) calc($gutter / 6) calc($gutter / -2);
					padding: $gutter 0 0 calc($gutter / 2);
					position: sticky;
					top: 0;
					width: 100%;
					z-index: 6;
				}

				.info-text {
					padding-top: calc($gutter * 1.5);
				}

				.protest {
					font-family: $roman-italic-book;
				}

				div {
					margin-bottom: calc($gutter / 2);
				}

				strong {
					display: inline-block;
					font-family: $roman-normal-bold;
					font-weight: normal;
				}

				p {
					padding-left: 15px;

					i {
						font-family: $roman-italic-book;
						font-variant: none;
					}
				}
			}
		}

		.close-panel {
			color: $teal;
			cursor: pointer;
			font-family: $roman-normal-bold;
			font-size: 2rem;
			font-weight: normal;
			top: -15px;
			left: -9px;
			position: absolute;
			text-transform: uppercase;
			z-index: 7;

			&:after {
				border: none;
			}
		}
	}

	#chartdiv {
		float: none;
		margin: 0 auto;
		max-width: 1600px;
		height: 650px;
	}

	.learn-more {
		p,
		li {
			font-family: $roman-normal-book;
		}

		.button-big {
			margin: $gutter auto calc($gutter * 4) auto;
			width: 20%;
		}
	}
	@include media-query(tablet) {
		.filter-area {height: 155px;}
		.filters form {
			padding: 0;
			fieldset label {
				margin: 0 5px 0 6px;
				padding: 5px;
			}
		}
		.filter-area {
            .filters .other-filters, .filters .participants-filters {float: none;}
        }
		#chartdiv {
			height: 550px;
		}
		:deep(.map #aboutTab #aboutTabContent) {
            background-color: $white;

            .about-overlay {
                .foreground {
                    margin: 0 30px;
                }
            }

            .top-section {
                height: 70px;
                padding: 15px 0;
            }

            .nav-tabs {
                margin-left: 0;
                width: auto;

                li {
                    a {
                        font-size: 1.1rem;
                        line-height: 1.1em;
                        padding-right: 30px;
                    }

                    &:last-child {
                        a {
                            padding-right: 0;
                        }
                    }
                }
            }

            .tab-content {
                font-size: 1.1rem;

                .tab-pane {
                    margin: 10px 0 0;
                    &:first-child {
                        p {
                            margin-bottom: 0;
                        }
                    }
                }
            }

            .close-button {
                display: block;
                float: left;
            }
        }

        :deep(.results-table table) {
            thead {top: 300px}
            thead th {
                &.responsive-hide {
                    display: none;
                }
                &.participants {
                    min-width: 0;
                }
                &.protest-name {
                    min-width: 35%;
                }
            }

            td {
                &:nth-child(3) {
                    padding-right: $gutter;
                    text-align: right;
                }

                &:nth-child(4) {
                    padding-right: 0;
                    text-align: left;
                }
                .foci > div {
                    float: left;
                }
            }
        }

        #info {
            &.active {
                width: 30%;
            }
        }
	}

	@include media-query(phone) {
        .hide-mobile {
            display: none;
        }

        .zone-title {
            background-position: 30% 70%;
            .container-title {
                width: 80%;
                h2 {font-size: 2rem;}
            }

            .cols .col {
                width: auto !important;
            }
        }


        .filter-area {
            height: 220px;
            top: 100px;

            .filters {
                margin: 0 auto;

                strong {font-size: 0.8rem; margin-left: 15px;}

                form {
                    padding-left: 15px;

                    fieldset {

                        .control-group {
                            text-align: center;
                        }

                        strong {
                            display: none;
                            float: none;
                            margin: 0;
                        }

                        label {
                            padding: 2px 5px;
                            span.name {font-size: 0.6rem;}
                        }
                    }
                }

                label.table-search {
                    width: auto;
                }
                .all-filters {
                    float: right;
                    width: 28%;
                    padding-left: 0;
                    strong {display: none;}
                    form {padding-left: 0;}
                    fieldset {
                        padding-left: 0;
                        padding-right: 0;
                        label:first-of-type {margin-left: 0;}
                    }
                }
                .participants-filters  {
                    float: left;
                    width: 70%;
                }
                .other-filters {
                    clear: both;
                    fieldset {
                        label:first-of-type {margin-left: 0;}
                    }
                }
            }

            &.section {
                margin-top: 0;
            }
        }

        .reponsive-mobile-hide {
            display: none;
        }

        #chartdiv {
            height: 280px;
        }
        #countrySelect {
            left: 25%;
        }

        #info {
            &.active {
                margin: $gutter auto;
                position: relative;
                right: 0;
                width: 90%;
            }
        }

        :deep(.map #aboutTab) {
            h3 {
                top: 20vh;

                span {
                    display: none;
                }
            }

            #aboutTabContent {
                width: 98vw;

                .top-section {
                    float: left;
                    padding: 45px 0;
                    width: 100%;

                    .foreground {
                        left: 60px;
                        top: -25px;
                        width: 74vw;
                    }
                }

                .nav-tabs li a {
                    font-size: 0.8rem;
                    padding: 0px 15px 5px 15px;
                }

                .about-overlay .foreground {
                    margin-right: 5px;
                }

                .section.about-overlay.section {
                    clear: both;
                    margin-left: 0;
                    float: none;
                    width: 100%;
                }

                .tab-content {
                    padding-top: 0;

                    .tab-pane {
                        .col.col-30 {
                            top: 0;
                        }
                        .col.col-70 {
                            margin-top: 30px;
                            top: 0;
                        }
                        p {
                            font-size: 1rem;
                        }
                    }
                }
            }
        }

        :deep(.results-table) {
			margin-bottom: 0;
            thead th {
                &.responsive-hide {
                    display: none;
                }
			}
            table {
                margin: 0;
                width: 100%;
                thead {
                    top: 320px;
                    .participants {display: none;}
                }

                td:nth-child(3) {
                    padding-right: 15px;
                }
                td.cols {
                    .col.col-20 {
                        width: 20% !important;
                        float: left;
                    }
                    .col.col-80 {
                        width: 80% !important;
                        float: left;
                    }
                }
                tr.secondary {
                    div:nth-child(2) {padding-top: 15px;}
                    .details {clear: both;}
                }
            }
        }

        .learn-more .button-big {
            width: 60%;
        }

        .download-buttons {
            background-color: $white;
            margin: 0;
            padding: 30px 0;
            position: relative;
            .button:first-child {
                margin-bottom: 15px;
            }
        }
    }

}
</style>
